import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getLotSize, isMarketOpen as checkMarketOpen, getSettings } from '../useSettings';
import { useLiveData } from '../LiveDataContext';
import OptionAnalysis from './OptionAnalysis';

const LAMBDA_URL = 'https://api.algrok.com';

function useWindowWidth() {
  var get = function() { return window.innerWidth; };
  var [w, setW] = useState(get());
  useEffect(function() { var h = function() { setW(get()); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, []);
  return w;
}

const SYMBOLS = [
  { label:'NIFTY 50',  value:'NIFTY 50',  index:'NIFTY',     exchange:'NFO', step:50 },
  { label:'BANKNIFTY', value:'BANKNIFTY', index:'BANKNIFTY', exchange:'NFO', step:100 },
  { label:'FINNIFTY',  value:'FINNIFTY',  index:'FINNIFTY',  exchange:'NFO', step:50 },
  { label:'SENSEX',    value:'SENSEX',    index:'SENSEX',    exchange:'BFO', step:100 },
];

function nowIST() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}
var isMarketOpen = checkMarketOpen;

function fmtOI(n) {
  if (n === null || n === undefined) return '\u2014';
  if (n === 0) return '0';
  if (Math.abs(n) >= 1e7) return (n / 1e7).toFixed(2) + 'Cr';
  if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(2) + 'L';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toLocaleString();
}
function fmtNum(n, dec) {
  if (dec === undefined) dec = 2;
  if (n === null || n === undefined) return '\u2014';
  return Number(n).toFixed(dec);
}
function fmtPct(n) {
  if (n === null || n === undefined) return '\u2014';
  var v = Number(n);
  if (isNaN(v)) return '\u2014';
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
}
function pnlColor(v) {
  if (!v && v !== 0) return '#6b7a8d';
  return v > 0 ? '#00d4aa' : v < 0 ? '#ff4466' : '#8899aa';
}
function oiChangeColor(v) {
  if (!v) return '#6b7a8d';
  return v > 0 ? '#4ade80' : '#f87171';
}

/* ── pick(obj, keys) — return first non-null value from a list of possible field names ── */
function pick(obj, keys) {
  if (!obj) return null;
  for (var i = 0; i < keys.length; i++) {
    var v = obj[keys[i]];
    if (v !== null && v !== undefined) return v;
  }
  return null;
}
function pickNum(obj, keys) {
  var v = pick(obj, keys);
  if (v === null || v === undefined) return null;
  var n = Number(v);
  return isNaN(n) ? null : n;
}

/* ── Black-Scholes IV calculation ─────────────────── */
function normCDF(x) {
  var a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  var a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  var sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.SQRT2;
  var t = 1.0 / (1.0 + p * x);
  var y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}
function bsPrice(S, K, T, r, sigma, isCall) {
  if (T <= 0 || sigma <= 0) return 0;
  var d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
  var d2 = d1 - sigma * Math.sqrt(T);
  if (isCall) {
    return S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);
  } else {
    return K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);
  }
}
function calcIV(S, K, T, r, marketPrice, isCall) {
  if (!S || !K || !T || T <= 0 || !marketPrice || marketPrice <= 0) return null;
  // Newton-Raphson
  var sigma = 0.3; // initial guess
  for (var i = 0; i < 50; i++) {
    var price = bsPrice(S, K, T, r, sigma, isCall);
    var d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
    var vega = S * Math.sqrt(T) * Math.exp(-d1 * d1 / 2) / Math.sqrt(2 * Math.PI);
    if (vega < 1e-10) break;
    var diff = price - marketPrice;
    sigma = sigma - diff / vega;
    if (sigma <= 0.001) sigma = 0.001;
    if (sigma > 5) sigma = 5;
    if (Math.abs(diff) < 0.01) break;
  }
  return sigma > 0.001 && sigma < 5 ? sigma * 100 : null; // return as percentage
}

/* ── Normalize a single row from whatever format the backend sends ── */
function normalizeRow(raw) {
  return {
    strike:       pickNum(raw, ['strike', 'strikePrice', 'strike_price']) || 0,
    ce_token:     pick(raw, ['ce_token', 'CE_token', 'call_token', 'ce_instrument_token']),
    pe_token:     pick(raw, ['pe_token', 'PE_token', 'put_token', 'pe_instrument_token']),
    ce_ltp:       pickNum(raw, ['ce_ltp', 'CE_LTP', 'CE_lastPrice', 'ce_last_price', 'call_ltp']),
    pe_ltp:       pickNum(raw, ['pe_ltp', 'PE_LTP', 'PE_lastPrice', 'pe_last_price', 'put_ltp']),
    ce_oi:        pickNum(raw, ['ce_oi', 'CE_OI', 'CE_openInterest', 'ce_open_interest', 'call_oi']) || 0,
    pe_oi:        pickNum(raw, ['pe_oi', 'PE_OI', 'PE_openInterest', 'pe_open_interest', 'put_oi']) || 0,
    ce_volume:    pickNum(raw, ['ce_volume', 'CE_volume', 'CE_totalTradedVolume', 'ce_total_traded_volume', 'call_volume']) || 0,
    pe_volume:    pickNum(raw, ['pe_volume', 'PE_volume', 'PE_totalTradedVolume', 'pe_total_traded_volume', 'put_volume']) || 0,
    ce_iv:        pickNum(raw, ['ce_iv', 'CE_IV', 'CE_impliedVolatility', 'ce_implied_volatility', 'call_iv']),
    pe_iv:        pickNum(raw, ['pe_iv', 'PE_IV', 'PE_impliedVolatility', 'pe_implied_volatility', 'put_iv']),
    ce_change_pct: pickNum(raw, ['ce_change_pct', 'CE_pChange', 'ce_pchange', 'ce_change_percent', 'call_change_pct']),
    pe_change_pct: pickNum(raw, ['pe_change_pct', 'PE_pChange', 'pe_pchange', 'pe_change_percent', 'put_change_pct']),
    ce_oi_change: pickNum(raw, ['ce_oi_chg', 'ce_oi_change', 'CE_changeinOpenInterest', 'ce_change_in_oi', 'call_oi_change']),
    pe_oi_change: pickNum(raw, ['pe_oi_chg', 'pe_oi_change', 'PE_changeinOpenInterest', 'pe_change_in_oi', 'put_oi_change']),
    ce_bid:       pickNum(raw, ['ce_bid', 'CE_bidprice', 'ce_bid_price']),
    pe_bid:       pickNum(raw, ['pe_bid', 'PE_bidprice', 'pe_bid_price']),
    ce_ask:       pickNum(raw, ['ce_ask', 'CE_askprice', 'ce_ask_price']),
    pe_ask:       pickNum(raw, ['pe_ask', 'PE_askprice', 'pe_ask_price']),
    // Also try NSE-style nested objects
    _raw: raw,
  };
}

/* ── Also handle NSE-format where CE and PE are nested objects ── */
function normalizeNSERow(raw) {
  var ce = raw.CE || raw.ce || raw.call || {};
  var pe = raw.PE || raw.pe || raw.put || {};
  return {
    strike:       pickNum(raw, ['strikePrice', 'strike_price', 'strike']) || 0,
    ce_token:     pick(ce, ['instrument_token', 'token', 'instrumentToken']),
    pe_token:     pick(pe, ['instrument_token', 'token', 'instrumentToken']),
    ce_ltp:       pickNum(ce, ['lastPrice', 'last_price', 'ltp', 'LTP']),
    pe_ltp:       pickNum(pe, ['lastPrice', 'last_price', 'ltp', 'LTP']),
    ce_oi:        pickNum(ce, ['openInterest', 'oi', 'OI', 'open_interest']),
    pe_oi:        pickNum(pe, ['openInterest', 'oi', 'OI', 'open_interest']),
    ce_volume:    pickNum(ce, ['totalTradedVolume', 'volume', 'volume_traded']),
    pe_volume:    pickNum(pe, ['totalTradedVolume', 'volume', 'volume_traded']),
    ce_iv:        pickNum(ce, ['impliedVolatility', 'iv', 'IV']),
    pe_iv:        pickNum(pe, ['impliedVolatility', 'iv', 'IV']),
    ce_change_pct: pickNum(ce, ['pChange', 'change_pct', 'changePct']),
    pe_change_pct: pickNum(pe, ['pChange', 'change_pct', 'changePct']),
    ce_oi_change: pickNum(ce, ['changeinOpenInterest', 'oi_change', 'change_in_oi']),
    pe_oi_change: pickNum(pe, ['changeinOpenInterest', 'oi_change', 'change_in_oi']),
    ce_bid:       pickNum(ce, ['bidprice', 'bid', 'bid_price']),
    pe_bid:       pickNum(pe, ['bidprice', 'bid', 'bid_price']),
    ce_ask:       pickNum(ce, ['askprice', 'ask', 'ask_price']),
    pe_ask:       pickNum(pe, ['askprice', 'ask', 'ask_price']),
    _raw: raw,
  };
}

function OIBar({ value, max, side }) {
  var pct = max > 0 ? Math.min(100, (Math.abs(value || 0) / max) * 100) : 0;
  return (
    <div style={{ width: '100%', height: 5, background: 'rgba(255,255,255,0.03)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{
        width: pct + '%', height: '100%', borderRadius: 2,
        background: side === 'CE'
          ? 'linear-gradient(90deg, rgba(255,68,102,0.3), rgba(255,68,102,0.7))'
          : 'linear-gradient(90deg, rgba(0,212,170,0.3), rgba(0,212,170,0.7))',
        float: side === 'CE' ? 'right' : 'left',
        transition: 'width 0.3s ease',
      }} />
    </div>
  );
}

function FlashCell({ value, format, prevRef, flashKey }) {
  var displayed = format ? format(value) : value;
  var prevVal = prevRef.current[flashKey];
  var flash = null;
  if (prevVal !== undefined && prevVal !== value && value != null) {
    flash = value > prevVal ? 'up' : value < prevVal ? 'down' : null;
  }
  if (value != null) prevRef.current[flashKey] = value;
  var flashBg = flash === 'up' ? 'rgba(0,212,170,0.2)' : flash === 'down' ? 'rgba(255,68,102,0.2)' : 'transparent';
  return (
    <span style={{ background: flashBg, transition: 'background 0.4s ease', borderRadius: 2, padding: '1px 3px' }}>
      {displayed}
    </span>
  );
}

/* ── Flow Popup helpers ─────────────────────────────
   Click any strike cell to open. Fetches /chain-flow/strike/<strike>?lookback=<min>.
   Shows CE/PE side-by-side metrics, status badges, driver, sparklines.
   Auto-refreshes every 10s while open. */
function statusBadge(status) {
  var map = {
    'BUILDING':         { bg: 'rgba(34,197,94,0.15)',  color: '#4ade80', label: 'Building' },
    'BUILDING_STRONG':  { bg: 'rgba(34,197,94,0.28)',  color: '#4ade80', label: 'Building+' },
    'UNWINDING':        { bg: 'rgba(251,146,60,0.15)', color: '#fb923c', label: 'Unwind' },
    'UNWINDING_STRONG': { bg: 'rgba(251,146,60,0.28)', color: '#fb923c', label: 'Unwind+' },
    'CHURN':            { bg: 'rgba(167,139,250,0.15)', color: '#a78bfa', label: 'Churn' },
    'STALE':            { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8', label: 'Stale' },
    'NO_HISTORY':       { bg: 'rgba(100,116,139,0.08)', color: '#6b7a8d', label: 'No data yet' },
  };
  var s = map[status] || map['NO_HISTORY'];
  return <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>;
}

function driverLabel(driver) {
  var map = {
    'WRITERS':     { color: '#4ade80', label: 'Writers supplying' },
    'BUYERS':      { color: '#fb923c', label: 'Buyers paying up' },
    'SHORT_COVER': { color: '#ef4444', label: 'Short covering' },
    'LIQUIDATION': { color: '#94a3b8', label: 'Orderly liquidation' },
    'AMBIGUOUS':   { color: '#6b7a8d', label: 'No clear driver' },
    'NO_HISTORY':  { color: '#6b7a8d', label: '\u2014' },
  };
  var d = map[driver] || map['AMBIGUOUS'];
  return <span style={{ color: d.color, fontSize: 10, fontWeight: 500 }}>{d.label}</span>;
}

function MiniSparkline({ values, color }) {
  if (!values || values.length < 2) {
    return <div style={{ fontSize: 8, color: '#6b7a8d' }}>{'\u2014'}</div>;
  }
  var mn = Math.min.apply(null, values);
  var mx = Math.max.apply(null, values);
  var rng = (mx - mn) || 1;
  var w = 120, h = 30;
  var pts = values.map(function(v, i) {
    var x = (i / (values.length - 1)) * w;
    var y = h - ((v - mn) / rng) * (h - 4) - 2;
    return x.toFixed(1) + ',' + y.toFixed(1);
  }).join(' ');
  var zeroY = h - ((0 - mn) / rng) * (h - 4) - 2;
  return (
    <svg width={w} height={h} style={{ display: 'inline-block' }}>
      <line x1="0" y1={zeroY} x2={w} y2={zeroY} stroke="rgba(100,116,139,0.3)" strokeWidth="0.5" strokeDasharray="2 2" />
      <polyline points={pts} stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function FlowPopup(props) {
  var strike = props.strike;
  var anchorY = props.anchorY;
  var lookback = props.lookback;
  var onClose = props.onClose;
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var pollRef = useRef(null);
  var popupRef = useRef(null);

  var fetchFlow = useCallback(function() {
    fetch(LAMBDA_URL + '/chain-flow/strike/' + strike + '?lookback=' + lookback)
      .then(function(r) { return r.json(); })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [strike, lookback]);

  useEffect(function() {
    fetchFlow();
    pollRef.current = setInterval(fetchFlow, 10000);
    return function() { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchFlow]);

  useEffect(function() {
    var handler = function(e) {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Defer so the click that opened the popup doesn't immediately close it
    var t = setTimeout(function() { document.addEventListener('mousedown', handler); }, 50);
    return function() { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  var top = Math.max(20, Math.min((typeof window !== 'undefined' ? window.innerHeight : 800) - 380, (anchorY || 100) - 60));
  var left = Math.max(20, ((typeof window !== 'undefined' ? window.innerWidth : 1280) / 2) - 180);

  var ce = data && data.ce;
  var pe = data && data.pe;
  var hasError = data && data.error;

  var ceSparkValues = data && data.history ? data.history.map(function(h) { return h.ce_d_oi; }) : [];
  var peSparkValues = data && data.history ? data.history.map(function(h) { return h.pe_d_oi; }) : [];

  return (
    <div ref={popupRef} style={{
      position: 'fixed', top: top, left: left, width: 360,
      background: '#0a0f1e', border: '1px solid rgba(14,165,233,0.5)',
      borderRadius: 8, padding: 14, zIndex: 9998,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      color: '#c4cfd8',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(26,45,74,0.5)' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e8f0' }}>{strike} flow detail</div>
          <div style={{ fontSize: 9, color: '#6b7a8d', marginTop: 2 }}>
            {lookback}m window
            {data && data.current_time ? ' \u00B7 last snap ' + (data.current_time.split('T')[1] || data.current_time) : ''}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(26,45,74,0.5)', color: '#6b7a8d', borderRadius: 3, fontSize: 11, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>{'\u00d7'}</button>
      </div>

      {loading && <div style={{ fontSize: 10, color: '#6b7a8d', padding: '20px 0', textAlign: 'center' }}>Loading flow data...</div>}

      {hasError && (
        <div style={{ fontSize: 10, color: '#fbbf24', padding: '14px 0', lineHeight: 1.6 }}>
          {data.error === 'no snapshots yet' ? 'No snapshots captured yet. Chain Flow takes a snapshot every 5 minutes during market hours. Wait for the first window to populate.' : data.error}
        </div>
      )}

      {!loading && !hasError && ce && pe && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[{ label: 'CE side', accent: '#fb923c', data: ce },
              { label: 'PE side', accent: '#10b981', data: pe }].map(function(side, i) {
              return (
                <div key={i} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 4, padding: '8px 10px' }}>
                  <div style={{ fontSize: 9, color: side.accent, fontWeight: 500, marginBottom: 6 }}>{side.label}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0' }}>
                    <span style={{ color: '#6b7a8d' }}>VOR</span>
                    <span style={{ color: side.data.vor >= 0.3 ? '#4ade80' : side.data.vor < 0.05 ? '#ef4444' : '#c4cfd8' }}>{side.data.vor.toFixed(3)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0' }}>
                    <span style={{ color: '#6b7a8d' }}>{'\u0394'}Vol</span>
                    <span style={{ color: side.data.d_vol > 0 ? '#4ade80' : '#6b7a8d' }}>{side.data.d_vol > 0 ? '+' : ''}{fmtOI(side.data.d_vol)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0' }}>
                    <span style={{ color: '#6b7a8d' }}>{'\u0394'}OI</span>
                    <span style={{ color: side.data.d_oi > 0 ? '#4ade80' : side.data.d_oi < 0 ? '#ef4444' : '#6b7a8d' }}>{side.data.d_oi > 0 ? '+' : ''}{fmtOI(side.data.d_oi)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0' }}>
                    <span style={{ color: '#6b7a8d' }}>{'\u0394'}IV</span>
                    <span style={{ color: side.data.d_iv_vp > 0 ? '#fb923c' : side.data.d_iv_vp < 0 ? '#4ade80' : '#6b7a8d' }}>{side.data.d_iv_vp > 0 ? '+' : ''}{side.data.d_iv_vp.toFixed(1)}vp</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, padding: '2px 0' }}>
                    <span style={{ color: '#6b7a8d' }}>Status</span>
                    {statusBadge(side.data.status)}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(26,45,74,0.4)' }}>
            <div style={{ fontSize: 9, color: '#556677', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Driver</div>
            <div style={{ fontSize: 10, lineHeight: 1.6, color: '#8899aa' }}>
              <span style={{ color: '#fb923c' }}>CE</span>: {driverLabel(ce.driver)}<br/>
              <span style={{ color: '#10b981' }}>PE</span>: {driverLabel(pe.driver)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(26,45,74,0.4)' }}>
            <div>
              <div style={{ fontSize: 9, color: '#6b7a8d' }}>CE {'\u0394'}OI history</div>
              <MiniSparkline values={ceSparkValues} color="#fb923c" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#6b7a8d' }}>PE {'\u0394'}OI history</div>
              <MiniSparkline values={peSparkValues} color="#10b981" />
            </div>
          </div>
          <div style={{ fontSize: 8, color: '#3d4f63', marginTop: 8, fontStyle: 'italic', lineHeight: 1.4 }}>
            OI updates from broker every {'\u2248'}3 min. Snapshots taken every 5 min during market hours.
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
export default function OptionChain() {
  var { ticks, subscribeTokens, connected } = useLiveData();
  var winW = useWindowWidth();
  var isCompact = winW < 1080;  // iPad and smaller — hide Vol, OI Bar columns
  var isTight = winW < 900;     // Very tight — also reduce font sizes
  var [sym, setSym] = useState(SYMBOLS[0]);
  var [expiries, setExpiries] = useState([]);
  var [selExpiry, setSelExpiry] = useState('');
  var [chain, setChain] = useState([]);
  var [spot, setSpot] = useState(null);
  var [loading, setLoading] = useState(false);
  var [lastUpdate, setLastUpdate] = useState(null);
  var [orderLog, setOrderLog] = useState([]);
  var [autoRefresh, setAutoRefresh] = useState(true);
  // ── refreshRate: persisted in localStorage, validated against allowed values ──
  var [refreshRate, setRefreshRate] = useState(function() {
    try {
      var v = localStorage.getItem('oc_refresh_rate');
      var n = v != null ? parseInt(v, 10) : 3000;
      return [1000, 2000, 3000, 5000].indexOf(n) >= 0 ? n : 3000;
    } catch (e) { return 3000; }
  });
  var [lots, setLots] = useState(1);
  // ── product (MIS/NRML): persisted in localStorage ──
  var [product, setProduct] = useState(function() {
    try {
      var v = localStorage.getItem('oc_product');
      return (v === 'MIS' || v === 'NRML') ? v : 'NRML';
    } catch (e) { return 'NRML'; }
  });
  var [orderType, setOrderType] = useState('MARKET');
  var [depthCount, setDepthCount] = useState(20);
  var [mainTab, setMainTab] = useState('chain');
  var [positions, setPositions] = useState([]);
  var [posLoading, setPosLoading] = useState(false);
  var [confirmOrder, setConfirmOrder] = useState(null);
  var [rawDebug, setRawDebug] = useState(null);
  var [showDebug, setShowDebug] = useState(false);
  var [showAnalysis, setShowAnalysis] = useState(false);
  var [dataSource, setDataSource] = useState('unknown');
  var [builderLegs, setBuilderLegs] = useState([]);
  var [showBuilder, setShowBuilder] = useState(false);
  var [ocState, setOcState] = useState(null);

  // Flow popup state (Chain Flow framework)
  var [flowPopup, setFlowPopup] = useState(null);   // { strike, anchorY } or null
  // ── flowLookback: persisted in localStorage, validated against allowed values ──
  var [flowLookback, setFlowLookback] = useState(function() {
    try {
      var v = localStorage.getItem('oc_flow_lookback');
      var n = v != null ? parseInt(v, 10) : 15;
      return [5, 10, 15, 30].indexOf(n) >= 0 ? n : 15;
    } catch (e) { return 15; }
  });

  // Chain-level Flow Summary state (Volume PCR divergence + Hot vs Pin)
  var [showFlowPanel, setShowFlowPanel] = useState(false); // collapsed by default
  var [gexState, setGexState] = useState(null);            // { pin_strike } from /gex/state

  var prevOI = useRef({});
  var prevPrices = useRef({});
  var cfg = getSettings();
  var forcedOpen = cfg.forceOpen;
  var forcedDays = cfg.weekendMarketDays && cfg.weekendMarketDays.length > 0;
  var atmRef = useRef(null);
  var userScrolled = useRef(false);
  var fetchCountRef = useRef(0);

  var addLog = useCallback(function(msg, type) {
    type = type || 'info';
    var ts = nowIST().toLocaleTimeString('en-IN', { hour12: false });
    setOrderLog(function(p) { return [{ ts: ts, msg: msg, type: type, id: Date.now() + Math.random() }].concat(p).slice(0, 100); });
  }, []);

  /* ── Time to expiry in years ─────────────────────── */
  function getTimeToExpiry(expiryStr) {
    var exp = new Date(expiryStr);
    exp.setHours(15, 30, 0, 0);
    var now = nowIST();
    var diffMs = exp.getTime() - now.getTime();
    if (diffMs <= 0) return 0.0001; // tiny positive for expired
    return diffMs / (365.25 * 24 * 60 * 60 * 1000);
  }

  /* ── Fetch Expiries ─────────────────────────────── */
  var fetchExpiries = useCallback(function(index, exchange) {
    return fetch(LAMBDA_URL + '/expiry?index=' + index + '&exchange=' + exchange)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.all_expiries) {
          setExpiries(d.all_expiries.map(function(e, i) {
            return {
              date: e,
              label: formatExpLabel(e),
              tag: i === 0 ? 'Weekly' : i === 1 ? 'Next' : 'Monthly',
            };
          }));
          setSelExpiry(d.all_expiries[0]);
        }
      })
      .catch(function(e) { addLog('Expiry fetch error: ' + e.message, 'error'); });
  }, [addLog]);

  function formatExpLabel(dateStr) {
    var d = new Date(dateStr);
    var dd = String(d.getDate()).padStart(2, '0');
    var mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    return dd + ' ' + mon;
  }

  /* ── Fetch Option Chain with field normalization ── */
  var fetchChain = useCallback(function(silent) {
    if (!selExpiry) return;
    if (!silent) setLoading(true);
    var fetchId = ++fetchCountRef.current;

    var url = LAMBDA_URL + '/optionchain?index=' + encodeURIComponent(sym.index) +
      '&expiry=' + selExpiry + '&exchange=' + sym.exchange + '&depth=' + depthCount;

    return fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (fetchId !== fetchCountRef.current) return;
        if (d.error) throw new Error(d.error);

        // Save first row for debug
        setRawDebug(d);

        var spotVal = pickNum(d, ['spot', 'spotPrice', 'spot_price', 'underlyingValue']);
        if (spotVal) setSpot(spotVal);

        // Detect data format
        var rawChain = d.chain || d.data || d.records || d.filtered || d.optionChain || [];
        if (!Array.isArray(rawChain)) {
          // NSE-style: might be nested
          if (d.records && d.records.data) rawChain = d.records.data;
          else if (d.filtered && d.filtered.data) rawChain = d.filtered.data;
          else rawChain = [];
        }

        if (rawChain.length === 0) {
          setDataSource('empty (' + Object.keys(d).join(',') + ')');
          addLog('Chain returned 0 rows. Keys: ' + Object.keys(d).join(', '), 'error');
          return;
        }

        // Detect format: flat (ce_oi, pe_oi) vs nested (CE: {oi}, PE: {oi})
        var sample = rawChain[0];
        var isNested = !!(sample.CE || sample.ce || sample.call || sample.PE || sample.pe || sample.put);
        var isFlat = sample.ce_oi !== undefined || sample.ce_ltp !== undefined || sample.CE_OI !== undefined;

        var normalizer = isNested ? normalizeNSERow : normalizeRow;
        setDataSource(isNested ? 'nested/NSE' : isFlat ? 'flat' : 'unknown');

        var T = getTimeToExpiry(selExpiry);
        var riskFreeRate = 0.07; // 7% India

        var tokenList = [];
        var rows = rawChain.map(function(raw) {
          var row = normalizer(raw);
          if (!row.strike) return null;

          if (row.ce_token) tokenList.push(row.ce_token);
          if (row.pe_token) tokenList.push(row.pe_token);

          // Calculate OI change from previous fetch if not provided by backend
          var key = String(row.strike);
          var prev = prevOI.current[key] || {};
          if (row.ce_oi_change === null && row.ce_oi != null && prev.ce_oi != null) {
            row.ce_oi_change = row.ce_oi - prev.ce_oi;
          }
          if (row.pe_oi_change === null && row.pe_oi != null && prev.pe_oi != null) {
            row.pe_oi_change = row.pe_oi - prev.pe_oi;
          }

          // Calculate IV if not provided and we have spot + ltp
          if (row.ce_iv === null && row.ce_ltp && spotVal && row.strike) {
            row.ce_iv = calcIV(spotVal, row.strike, T, riskFreeRate, row.ce_ltp, true);
          }
          if (row.pe_iv === null && row.pe_ltp && spotVal && row.strike) {
            row.pe_iv = calcIV(spotVal, row.strike, T, riskFreeRate, row.pe_ltp, false);
          }

          return row;
        }).filter(Boolean);

        // Sort by strike
        rows.sort(function(a, b) { return a.strike - b.strike; });

        // Filter to ±depth strikes around ATM (backend may send ALL strikes)
        if (spotVal && rows.length > depthCount * 2) {
          var atmIdx = 0;
          var minDist = Infinity;
          for (var i = 0; i < rows.length; i++) {
            var dist = Math.abs(rows[i].strike - spotVal);
            if (dist < minDist) { minDist = dist; atmIdx = i; }
          }
          var startIdx = Math.max(0, atmIdx - depthCount);
          var endIdx = Math.min(rows.length, atmIdx + depthCount + 1);
          rows = rows.slice(startIdx, endIdx);
        }

        // Update prevOI baseline
        rows.forEach(function(row) {
          prevOI.current[String(row.strike)] = {
            ce_oi: row.ce_oi,
            pe_oi: row.pe_oi,
          };
        });

        setChain(rows);
        setLastUpdate(nowIST().toLocaleTimeString('en-IN', { hour12: false }));

        if (subscribeTokens && tokenList.length > 0) {
          subscribeTokens(tokenList);
        }

        if (!userScrolled.current && !silent) {
          setTimeout(function() {
            if (atmRef.current) atmRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 150);
        }
      })
      .catch(function(e) {
        if (fetchId === fetchCountRef.current) {
          addLog('Chain error: ' + e.message, 'error');
        }
      })
      .finally(function() {
        if (fetchId === fetchCountRef.current && !silent) setLoading(false);
      });
  }, [selExpiry, sym, addLog, depthCount, subscribeTokens]);

  /* ── Overlay live ticks — ONLY override if tick has actual data ── */
  var liveChain = useMemo(function() {
    if (!chain.length) return chain;
    return chain.map(function(row) {
      var ceTick = row.ce_token ? ticks[String(row.ce_token)] || ticks[row.ce_token] : null;
      var peTick = row.pe_token ? ticks[String(row.pe_token)] || ticks[row.pe_token] : null;

      // SAFE overlay: only replace if tick field is a real number
      var result = Object.assign({}, row);
      if (ceTick) {
        if (typeof ceTick.ltp === 'number' && ceTick.ltp > 0) result.ce_ltp = ceTick.ltp;
        if (typeof ceTick.last_price === 'number' && ceTick.last_price > 0) result.ce_ltp = ceTick.last_price;
        if (typeof ceTick.oi === 'number' && ceTick.oi > 0) result.ce_oi = ceTick.oi;
        if (typeof ceTick.volume === 'number' && ceTick.volume > 0) result.ce_volume = ceTick.volume;
        if (typeof ceTick.volume_traded === 'number' && ceTick.volume_traded > 0) result.ce_volume = ceTick.volume_traded;
        if (typeof ceTick.change_pct === 'number') result.ce_change_pct = ceTick.change_pct;
      }
      if (peTick) {
        if (typeof peTick.ltp === 'number' && peTick.ltp > 0) result.pe_ltp = peTick.ltp;
        if (typeof peTick.last_price === 'number' && peTick.last_price > 0) result.pe_ltp = peTick.last_price;
        if (typeof peTick.oi === 'number' && peTick.oi > 0) result.pe_oi = peTick.oi;
        if (typeof peTick.volume === 'number' && peTick.volume > 0) result.pe_volume = peTick.volume;
        if (typeof peTick.volume_traded === 'number' && peTick.volume_traded > 0) result.pe_volume = peTick.volume_traded;
        if (typeof peTick.change_pct === 'number') result.pe_change_pct = peTick.change_pct;
      }
      return result;
    });
  }, [chain, ticks]);

  /* ── Fetch Positions ────────────────────────────── */
  var fetchPositions = useCallback(function() {
    setPosLoading(true);
    return fetch(LAMBDA_URL + '/positions')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) throw new Error(d.error);
        var all = d.net || (d.data && d.data.net) || d || [];
        var optPositions = (Array.isArray(all) ? all : []).filter(function(p) {
          return p.tradingsymbol && p.quantity !== 0 && (p.exchange === 'NFO' || p.exchange === 'BFO');
        });
        setPositions(optPositions);
      })
      .catch(function(e) { console.warn('Position fetch:', e.message); })
      .finally(function() { setPosLoading(false); });
  }, []);

  var fetchOcState = useCallback(function() {
    fetch(LAMBDA_URL + '/oc-state').then(function(r) { return r.json(); }).then(function(d) { setOcState(d); }).catch(function() {});
  }, []);

  // GEX state — used for the "Hot strike vs GEX pin" comparison in the Flow Summary
  var fetchGexState = useCallback(function() {
    fetch(LAMBDA_URL + '/gex/state').then(function(r) { return r.json(); }).then(function(d) {
      if (d && !d.error) {
        // GEX engine doesn't expose a single 'pin_strike' field. Derive it from per_strike
        // as the strike with the largest POSITIVE net_gex — pinning (dealer-hedge gravity
        // pulling spot back) only exists where dealers are long gamma. Using max |net_gex|
        // could select a strongly NEGATIVE-gamma strike, where hedging amplifies moves
        // away — the opposite of a pin — inverting the mean-reversion read.
        var pin = null;
        var pinReason = 'no_data';
        if (Array.isArray(d.per_strike) && d.per_strike.length > 0) {
          var top = null;
          var topG = 0;
          for (var i = 0; i < d.per_strike.length; i++) {
            var g = d.per_strike[i].net_gex || 0;
            if (g > topG) { topG = g; top = d.per_strike[i]; }
          }
          if (top) { pin = top.strike; pinReason = ''; }
          else { pinReason = 'negative_gamma'; }
        }
        d.pin_strike = pin;
        d.pin_reason = pinReason;
        setGexState(d);
      }
    }).catch(function() {});
  }, []);

  /* ── Effects ────────────────────────────────────── */
  // Persist UI preferences across page reloads / browser restarts
  useEffect(function() {
    try { localStorage.setItem('oc_product', product); } catch (e) {}
  }, [product]);
  useEffect(function() {
    try { localStorage.setItem('oc_refresh_rate', String(refreshRate)); } catch (e) {}
  }, [refreshRate]);
  useEffect(function() {
    try { localStorage.setItem('oc_flow_lookback', String(flowLookback)); } catch (e) {}
  }, [flowLookback]);

  useEffect(function() { fetchExpiries(sym.index, sym.exchange); }, [sym, fetchExpiries]);
  useEffect(function() { fetchOcState(); var id = setInterval(fetchOcState, 3000); return function() { clearInterval(id); }; }, [fetchOcState]);
  useEffect(function() { fetchGexState(); var id = setInterval(fetchGexState, 10000); return function() { clearInterval(id); }; }, [fetchGexState]);
  useEffect(function() {
    if (selExpiry) { userScrolled.current = false; fetchChain(); }
  }, [selExpiry, fetchChain]);

  useEffect(function() {
    if (!autoRefresh || !selExpiry) return;
    var id = setInterval(function() {
      if (isMarketOpen() || forcedOpen) fetchChain(true);
    }, refreshRate);
    return function() { clearInterval(id); };
  }, [fetchChain, autoRefresh, refreshRate, selExpiry, forcedOpen]);

  useEffect(function() {
    fetchPositions();
    var id = setInterval(fetchPositions, 10000);
    return function() { clearInterval(id); };
  }, [fetchPositions]);

  useEffect(function() {
    var t = ticks[sym.value];
    if (t && t.ltp) setSpot(t.ltp);
  }, [ticks, sym]);

  useEffect(function() {
    var onVisible = function() {
      if (document.visibilityState === 'visible' && selExpiry) { fetchChain(); fetchPositions(); fetchOcState(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    return function() { document.removeEventListener('visibilitychange', onVisible); };
  }, [selExpiry, fetchChain, fetchPositions]);

  /* ── Strategy Builder ─────────────────────────────── */
  var builderLotSize = getLotSize(sym.index);
  var builderQty = lots * builderLotSize;
  function addLeg(strike, type, side) {
    var row = liveChain.find(function(r) { return r.strike === strike; });
    var symbol = type === 'CE' ? (row && row.ce_symbol || '') : (row && row.pe_symbol || '');
    var token = type === 'CE' ? (row && row.ce_token || '') : (row && row.pe_token || '');
    var ltp = row ? (type === 'CE' ? (row.ce_ltp || 0) : (row.pe_ltp || 0)) : 0;
    if (!symbol && token) {
      fetch(LAMBDA_URL + '/tradingsymbol?token=' + token).then(function(r) { return r.json(); }).then(function(d) {
        if (d.tradingsymbol) {
          setBuilderLegs(function(prev) { return prev.map(function(l) { return l.token === token ? Object.assign({}, l, { symbol: d.tradingsymbol }) : l; }); });
        }
      }).catch(function() {});
    }
    setBuilderLegs(function(prev) {
      var key = symbol || (token ? 'tok_' + token : sym.index + strike + type);
      if (prev.find(function(l) { return l.symbol === key || l.token === token; })) return prev;
      return prev.concat([{ symbol: symbol || key, token: token, strike: strike, type: type, side: side, qty: builderQty, lots: lots, ltp: ltp }]);
    });
    setShowBuilder(true);
  }
  function removeLeg(sym2) { setBuilderLegs(function(prev) { return prev.filter(function(l) { return l.symbol !== sym2 && ('tok_' + l.token) !== sym2; }); }); }
  function toggleSide(idx) { setBuilderLegs(function(prev) { var n = prev.slice(); n[idx] = Object.assign({}, n[idx], { side: n[idx].side === 'BUY' ? 'SELL' : 'BUY' }); return n; }); }
  function setLegLots(idx, v) { setBuilderLegs(function(prev) { var n = prev.slice(); n[idx] = Object.assign({}, n[idx], { lots: Math.max(1, v), qty: Math.max(1, v) * builderLotSize }); return n; }); }
  function isInBuilder(row, type) {
    var s = type === 'CE' ? (row.ce_symbol || '') : (row.pe_symbol || '');
    var t = type === 'CE' ? (row.ce_token || '') : (row.pe_token || '');
    return builderLegs.some(function(l) { return (s && l.symbol === s) || (t && l.token === t); });
  }
  function placeStrategy() {
    if (!builderLegs.length) return;
    var unresolvedLegs = builderLegs.filter(function(l) { return !l.symbol || l.symbol.indexOf('tok_') === 0 || l.symbol.indexOf('2026') >= 0 || l.symbol.indexOf('2025') >= 0; });
    if (unresolvedLegs.length > 0) {
      addLog('Resolving ' + unresolvedLegs.length + ' symbols from tokens...', 'info');
      Promise.all(unresolvedLegs.map(function(l) {
        if (!l.token) return Promise.resolve(null);
        return fetch(LAMBDA_URL + '/tradingsymbol?token=' + l.token).then(function(r) { return r.json(); }).then(function(d) { return { token: l.token, symbol: d.tradingsymbol }; }).catch(function() { return null; });
      })).then(function(results) {
        var resolved = builderLegs.map(function(l) {
          var r = results.find(function(x) { return x && x.token === l.token; });
          if (r && r.symbol) return Object.assign({}, l, { symbol: r.symbol });
          return l;
        });
        var still = resolved.filter(function(l) { return !l.symbol || l.symbol.indexOf('tok_') === 0; });
        if (still.length > 0) { addLog('Could not resolve ' + still.length + ' symbols', 'error'); return; }
        doPlace(resolved);
      });
    } else {
      if (!window.confirm('Place ' + builderLegs.length + ' legs?\nBUY first, then SELL\nMode: ' + orderType)) return;
      doPlace(builderLegs);
    }
  }
  function doPlace(legs) {
    fetch(LAMBDA_URL + '/oc-place', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ legs: legs, order_mode: orderType.toLowerCase(), product: product, exchange: sym.exchange }) })
      .then(function() { setBuilderLegs([]); setShowBuilder(false); addLog('Strategy placed: ' + legs.length + ' legs', 'success'); setTimeout(fetchOcState, 1000); })
      .catch(function(e) { addLog('Place error: ' + e.message, 'error'); });
  }
  function fmtRs(n) { if (n == null || isNaN(n)) return '--'; var a = Math.abs(Math.round(n)); return (n >= 0 ? '+' : '-') + a.toLocaleString('en-IN'); }
  function ocDoApi(url, body) { fetch(LAMBDA_URL + url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }).then(function() { setTimeout(fetchOcState, 500); }).catch(function() {}); }

  var builderPayoff = useMemo(function() {
    if (!builderLegs.length || !spot) return [];
    var center = Math.round(spot / sym.step) * sym.step; var pts = [];
    for (var x = center - 800; x <= center + 800; x += 10) {
      var pnl = 0;
      builderLegs.forEach(function(l) { var intr = l.type === 'CE' ? Math.max(0, x - l.strike) : Math.max(0, l.strike - x); if (l.side === 'BUY') pnl += (intr - l.ltp) * l.qty; else pnl += (l.ltp - intr) * l.qty; });
      pts.push({ x: x, y: Math.round(pnl) });
    } return pts;
  }, [builderLegs, spot, sym.step]);
  var bpMin = builderPayoff.length ? Math.min.apply(null, builderPayoff.map(function(p) { return p.y; })) : 0;
  var bpMax = builderPayoff.length ? Math.max.apply(null, builderPayoff.map(function(p) { return p.y; })) : 0;
  var builderGreeks = useMemo(function() {
    var nd = 0, nt = 0, credit = 0;
    builderLegs.forEach(function(l) { var m = l.side === 'BUY' ? 1 : -1; var mn = spot ? (spot - l.strike) / (spot * 0.15 * Math.sqrt(1 / 365)) : 0; var d = 1 / (1 + Math.exp(-0.7 * mn)); if (l.type === 'PE') d = d - 1; nd += d * m * l.qty; nt += l.ltp * 0.05 * (-m) * l.qty; credit += l.ltp * l.qty * (-m); });
    return { delta: Math.round(nd * 100) / 100, theta: Math.round(nt), credit: Math.round(credit) };
  }, [builderLegs, spot]);
  var ocPositions = ocState ? (ocState.positions || []).filter(function(l) { return l.status === 'open'; }) : [];
  var ocLog = ocState ? ocState.order_log || [] : [];
  var ocMtm = ocState ? ocState.total_mtm || 0 : 0;

  /* ── Place Order ────────────────────────────────── */
  var placeOrder = function(strike, optType, action, ltp) {
    var expDate = new Date(selExpiry);
    var today = nowIST();
    expDate.setHours(15, 30, 0, 0);
    if (today > expDate) { addLog('\u2717 Expiry ' + selExpiry + ' expired', 'error'); return; }

    var row = liveChain.find(function(r) { return r.strike === strike; });
    var token = optType === 'CE' ? (row && row.ce_token) : (row && row.pe_token);
    var symbol = optType === 'CE' ? (row && row.ce_symbol) : (row && row.pe_symbol);
    if (!token && !symbol) { addLog('\u2717 No token for ' + strike + optType, 'error'); return; }

    var qty = getLotSize(sym.index) * lots;

    var doOcPlace = function(tradingsymbol) {
      addLog('\u2192 ' + action + ' ' + lots + 'L ' + tradingsymbol + ' via OC executor', 'order');
      fetch(LAMBDA_URL + '/oc-place', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          legs: [{ symbol: tradingsymbol, strike: strike, type: optType, side: action, qty: qty, lots: lots }],
          order_mode: orderType.toLowerCase(), product: product, exchange: sym.exchange
        })
      }).then(function() {
        addLog('\u2713 Sent ' + action + ' ' + strike + optType + ' to OC executor', 'success');
        setTimeout(fetchOcState, 1000);
      }).catch(function(e) { addLog('\u2717 ' + e.message, 'error'); });
    };

    if (symbol && symbol.indexOf('2026') < 0 && symbol.indexOf('2025') < 0) {
      doOcPlace(symbol);
    } else if (token) {
      fetch(LAMBDA_URL + '/tradingsymbol?token=' + token)
        .then(function(r) { return r.json(); })
        .then(function(sd) {
          if (!sd.tradingsymbol) throw new Error('Symbol not found for ' + strike + optType);
          doOcPlace(sd.tradingsymbol);
        })
        .catch(function(e) { addLog('\u2717 ' + e.message, 'error'); });
    } else {
      addLog('\u2717 No symbol or token for ' + strike + optType, 'error');
    }
  };

  var handleOrderClick = function(strike, optType, action, ltp) {
    setConfirmOrder({ strike: strike, optType: optType, action: action, ltp: ltp });
  };

  // Open flow popup for a strike — anchored near the clicked row
  var handleStrikeClick = function(strike, e) {
    var rect = e.currentTarget.getBoundingClientRect();
    setFlowPopup({ strike: strike, anchorY: rect.top + (rect.height / 2) });
  };

  /* ── Computed ────────────────────────────────────── */
  var maxCEOI = 1, maxPEOI = 1;
  liveChain.forEach(function(r) {
    if ((r.ce_oi || 0) > maxCEOI) maxCEOI = r.ce_oi;
    if ((r.pe_oi || 0) > maxPEOI) maxPEOI = r.pe_oi;
  });
  var atmStrike = spot && liveChain.length ? liveChain.reduce(function(best, r) {
    return Math.abs(r.strike - spot) < Math.abs(best.strike - spot) ? r : best;
  }, liveChain[0]) : null;
  var totalCEOI = 0, totalPEOI = 0;
  liveChain.forEach(function(r) { totalCEOI += (r.ce_oi || 0); totalPEOI += (r.pe_oi || 0); });
  var pcr = totalCEOI > 0 ? totalPEOI / totalCEOI : 0;

  var maxPain = useMemo(function() {
    if (!liveChain.length) return null;
    var minPain = Infinity, mpStrike = null;
    liveChain.forEach(function(row) {
      var pain = 0;
      liveChain.forEach(function(r) {
        if (r.ce_oi && r.strike < row.strike) pain += r.ce_oi * (row.strike - r.strike);
        if (r.pe_oi && r.strike > row.strike) pain += r.pe_oi * (r.strike - row.strike);
      });
      if (pain < minPain) { minPain = pain; mpStrike = row.strike; }
    });
    return mpStrike;
  }, [liveChain]);

  /* ── Flow Summary: Volume PCR divergence + Hot strike vs GEX pin ──
     All metrics derive from liveChain + gexState. Memoized for cheap re-render. */
  var flowSummary = useMemo(function() {
    if (!liveChain.length) return null;

    // ── Aggregates ──
    var totalCeOi = 0, totalPeOi = 0, totalCeVol = 0, totalPeVol = 0;
    var hotCe = { strike: 0, vol: 0 };
    var hotPe = { strike: 0, vol: 0 };
    var perStrikeCombined = []; // for hottest strike by combined volume
    liveChain.forEach(function(r) {
      var cv = r.ce_volume || 0, pv = r.pe_volume || 0;
      var co = r.ce_oi || 0, po = r.pe_oi || 0;
      totalCeOi += co; totalPeOi += po;
      totalCeVol += cv; totalPeVol += pv;
      if (cv > hotCe.vol) { hotCe = { strike: r.strike, vol: cv }; }
      if (pv > hotPe.vol) { hotPe = { strike: r.strike, vol: pv }; }
      perStrikeCombined.push({ strike: r.strike, total: cv + pv, ce_vol: cv, pe_vol: pv });
    });

    var oiPcr = totalCeOi > 0 ? totalPeOi / totalCeOi : 0;
    var volPcr = totalCeVol > 0 ? totalPeVol / totalCeVol : 0;
    var divergence = volPcr - oiPcr; // positive = fresh put-buying, negative = fresh call-buying

    // Hottest strike: pick from CE/PE/combined whichever has the most concentrated flow
    // Use the dominant side as the "hot side" label
    perStrikeCombined.sort(function(a, b) { return b.total - a.total; });
    var hottest = perStrikeCombined[0] || { strike: 0, total: 0, ce_vol: 0, pe_vol: 0 };
    var hotSide = hottest.pe_vol > hottest.ce_vol ? 'PE' : 'CE';
    var hotPePct = hottest.total > 0 ? (hottest.pe_vol / hottest.total) * 100 : 50;

    // GEX pin
    var gexPin = gexState && gexState.pin_strike ? gexState.pin_strike : null;
    var gap = (gexPin && hottest.strike) ? hottest.strike - gexPin : null;

    // ── Top 5 VOR strikes ──
    // VOR per side: prefer the side with higher absolute volume per strike
    var vorList = [];
    liveChain.forEach(function(r) {
      var ceVor = (r.ce_oi || 0) > 0 ? (r.ce_volume || 0) / r.ce_oi : 0;
      var peVor = (r.pe_oi || 0) > 0 ? (r.pe_volume || 0) / r.pe_oi : 0;
      if ((r.ce_volume || 0) > 0 && ceVor > 0) vorList.push({ strike: r.strike, side: 'CE', vol: r.ce_volume, oi: r.ce_oi, vor: ceVor });
      if ((r.pe_volume || 0) > 0 && peVor > 0) vorList.push({ strike: r.strike, side: 'PE', vol: r.pe_volume, oi: r.pe_oi, vor: peVor });
    });
    vorList.sort(function(a, b) { return b.vor - a.vor; });
    var topVor = vorList.slice(0, 5);

    // ── Interpretation generators (deterministic lookup) ──
    // Volume PCR divergence thresholds (data-derived from typical Indian index option flow)
    var divInterp = null;
    if (Math.abs(divergence) < 0.15) {
      divInterp = { tone: 'neutral', label: 'Calm \u2014 today\u2019s flow aligns with positioning', desc: 'Volume PCR is close to OI PCR. No fresh directional pressure entering the market today.' };
    } else if (divergence >= 0.15 && divergence < 0.4) {
      divInterp = { tone: 'amber', label: 'Mild put-buying today', desc: 'Today\u2019s put volume is somewhat elevated vs accumulated put OI. Watch for follow-through.' };
    } else if (divergence >= 0.4) {
      divInterp = { tone: 'bear', label: 'Fresh put-buying detected', desc: 'Today\u2019s put volume is significantly higher than the accumulated put-OI baseline. Fresh fear is entering the market \u2014 often a leading indicator of a downside move that has only just started.' };
    } else if (divergence > -0.4 && divergence <= -0.15) {
      divInterp = { tone: 'amber', label: 'Mild call-buying today', desc: 'Today\u2019s call volume is somewhat elevated vs accumulated call OI. Watch for follow-through.' };
    } else { // divergence <= -0.4
      divInterp = { tone: 'bull', label: 'Fresh call-buying detected', desc: 'Today\u2019s call volume is significantly higher than the accumulated call-OI baseline. Fresh bullishness is entering the market \u2014 often a leading indicator of an upside move that has only just started.' };
    }

    // Hot strike vs GEX pin interpretation — depends on gap direction AND hot side
    var hotInterp = null;
    if (!gexPin) {
      if (gexState && gexState.pin_reason === 'negative_gamma') {
        hotInterp = { tone: 'amber', label: 'No positive-gamma pin today', desc: 'Every strike in the GEX band shows negative net gamma \u2014 dealer hedging amplifies moves rather than pinning spot. Mean-reversion-to-pin logic does not apply; treat today as a trend regime and lean on the divergence read instead.' };
      } else {
        hotInterp = { tone: 'neutral', label: 'GEX pin unavailable', desc: 'GEX module data not loaded. The hot-strike-vs-pin comparison can\u2019t be computed right now.' };
      }
    } else if (Math.abs(gap) <= 50) {
      hotInterp = { tone: 'neutral', label: 'Hot strike matches the pin', desc: 'Heaviest volume sits at or near the GEX pin. Today\u2019s flow agrees with the structural anchor \u2014 mean-reversion to the pin is the consensus expectation.' };
    } else if (gap > 50) {
      // Hot strike ABOVE pin
      if (hotSide === 'CE') {
        hotInterp = { tone: 'bull', label: 'Hot strike disagrees with pin', desc: 'Heaviest volume sits ' + Math.abs(gap) + 'pts above the GEX pin and is CE-dominant. Traders are paying up for calls above the structural anchor \u2014 they expect spot to break the pin upward.' };
      } else {
        hotInterp = { tone: 'bear', label: 'Hot strike disagrees with pin', desc: 'Heaviest volume sits ' + Math.abs(gap) + 'pts above the GEX pin and is PE-dominant. Traders are paying up for puts well above the structural anchor \u2014 either heavy upside hedging or expectation of a sharp move down through the pin.' };
      }
    } else { // gap < -50, hot strike BELOW pin
      if (hotSide === 'PE') {
        hotInterp = { tone: 'bear', label: 'Hot strike disagrees with pin', desc: 'Heaviest volume sits ' + Math.abs(gap) + 'pts below the GEX pin and is PE-dominant. Traders are paying up for puts below the structural anchor \u2014 they expect spot to break the pin downward.' };
      } else {
        hotInterp = { tone: 'bull', label: 'Hot strike disagrees with pin', desc: 'Heaviest volume sits ' + Math.abs(gap) + 'pts below the GEX pin and is CE-dominant. Either heavy downside hedging or expectation of a sharp move up through the pin.' };
      }
    }

    // Synthesis: combine the two reads into a regime call
    var synth = null;
    var divBear = divergence >= 0.4;
    var divBull = divergence <= -0.4;
    var hotBear = hotInterp.tone === 'bear';
    var hotBull = hotInterp.tone === 'bull';
    var hotNeutral = hotInterp.tone === 'neutral' && gexPin;

    if (divBear && hotBear) {
      synth = { tone: 'bear', label: 'Today\u2019s regime: downside pressure', desc: 'Volume-PCR divergence is bearish; hot strike is PE-dominant relative to the pin. Both signals agree: downside pressure, not mean-reversion. Short-straddle setups (which assume mean reversion) carry elevated risk today. Directional PE or put-spread structures are more aligned with the flow.' };
    } else if (divBull && hotBull) {
      synth = { tone: 'bull', label: 'Today\u2019s regime: upside pressure', desc: 'Volume-PCR divergence is bullish; hot strike is CE-dominant relative to the pin. Both signals agree: upside pressure, not mean-reversion. Short-straddle setups carry elevated risk today. Directional CE or call-spread structures are more aligned with the flow.' };
    } else if (hotNeutral && Math.abs(divergence) < 0.15) {
      synth = { tone: 'bull', label: 'Today\u2019s regime: pin in play', desc: 'PCR divergence is calm and hot strike matches the GEX pin. Today\u2019s flow agrees with the structural anchor \u2014 mean-reversion to the pin is the consensus. Short-straddle / iron-condor setups around the pin are aligned with the flow.' };
    } else if ((divBear && hotBull) || (divBull && hotBear)) {
      synth = { tone: 'amber', label: 'Today\u2019s regime: mixed signals', desc: 'PCR divergence and hot-strike-vs-pin point in opposite directions. The market lacks consensus today \u2014 don\u2019t force a directional read. Reduce size or wait for one signal to dominate before committing.' };
    } else if (gexState && gexState.pin_reason === 'negative_gamma') {
      synth = { tone: 'amber', label: 'Today\u2019s regime: negative gamma \u2014 trend risk', desc: 'No positive-gamma pin exists \u2014 dealer hedging amplifies moves in both directions. Mean-reversion setups (short straddles / condors) carry elevated risk today. Follow the divergence read for direction, or stand aside.' };
    } else {
      synth = { tone: 'amber', label: 'Today\u2019s regime: unclear', desc: 'Signals are not strong enough to call a directional regime. Stick to the structural read (GEX pin) and avoid leveraged directional bets until flow clarifies.' };
    }

    return {
      oiPcr: oiPcr, volPcr: volPcr, divergence: divergence,
      hottest: hottest, hotSide: hotSide, hotPePct: hotPePct,
      gexPin: gexPin, gap: gap,
      topVor: topVor,
      divInterp: divInterp, hotInterp: hotInterp, synth: synth,
      totalCeVol: totalCeVol, totalPeVol: totalPeVol,
    };
  }, [liveChain, gexState]);

  var positionsPnl = useMemo(function() {
    return positions.reduce(function(sum, p) {
      var liveLtp = (ticks[p.instrument_token] && ticks[p.instrument_token].ltp) || p.last_price || 0;
      return sum + (liveLtp - p.average_price) * p.quantity;
    }, 0);
  }, [positions, ticks]);

  /* ── Count data quality ─────────────────────────── */
  var dataStats = useMemo(function() {
    var oi = 0, iv = 0, ltp = 0, total = liveChain.length;
    liveChain.forEach(function(r) {
      if (r.ce_oi || r.pe_oi) oi++;
      if (r.ce_iv || r.pe_iv) iv++;
      if (r.ce_ltp || r.pe_ltp) ltp++;
    });
    return { oi: oi, iv: iv, ltp: ltp, total: total };
  }, [liveChain]);

  /* ── Styles ─────────────────────────────────────── */
  var S = {
    th: {
      padding: isTight ? '4px 4px' : '5px 6px', fontSize: isTight ? 8 : 9, color: '#556677', fontWeight: 800,
      textTransform: 'uppercase', letterSpacing: '0.6px',
      borderBottom: '1px solid rgba(26,45,74,0.6)', whiteSpace: 'nowrap',
      position: 'sticky', top: 0, background: '#0a0f1e', zIndex: 10,
    },
  };
  var td = function(extra) {
    return Object.assign({
      padding: isTight ? '3px 4px' : '4px 6px', fontSize: isTight ? 10 : 11, color: '#c4cfd8',
      borderBottom: '1px solid rgba(26,45,74,0.15)', whiteSpace: 'nowrap',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    }, extra || {});
  };
  var btnS = function(color) {
    return {
      padding: '2px 6px', borderRadius: 3, fontSize: 9, cursor: 'pointer',
      fontWeight: 800, lineHeight: '16px', minWidth: 20, textAlign: 'center',
      background: 'rgba(' + color + ',0.08)', color: 'rgb(' + color + ')',
      border: '1px solid rgba(' + color + ',0.3)', transition: 'all 0.15s',
    };
  };
  var pill = function(active, color) {
    color = color || '14,165,233';
    return {
      padding: '3px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer',
      fontWeight: 700, border: 'none', fontFamily: 'inherit',
      background: active ? 'rgba(' + color + ',0.2)' : 'rgba(26,45,74,0.2)',
      color: active ? 'rgb(' + color + ')' : '#6b7a8d',
    };
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: '#060b18', color: '#e2e8f0',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace", overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ padding: '8px 12px', background: '#0a0f1e', borderBottom: '1px solid rgba(26,45,74,0.6)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 130 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>
              Option Chain
              <span style={{
                fontSize: 8, marginLeft: 6, padding: '2px 5px', borderRadius: 3,
                background: connected ? 'rgba(0,212,170,0.15)' : 'rgba(255,68,102,0.15)',
                color: connected ? '#00d4aa' : '#ff4466', verticalAlign: 'middle',
              }}>
                {connected ? '\u25CF LIVE' : '\u25CB POLL'}
              </span>
            </div>
            <div style={{ fontSize: 9, color: '#556677', marginTop: 1 }}>
              {depthCount}-depth · {isMarketOpen() ? '\uD83D\uDFE2 Open' : '\uD83D\uDD34 Closed'}
              {lastUpdate && (' \u00B7 ' + lastUpdate)}
              {' \u00B7 '}
              <span style={{ color: dataStats.oi > 0 ? '#4ade80' : '#f87171' }}>
                OI:{dataStats.oi}/{dataStats.total}
              </span>
              {' '}
              <span style={{ color: dataStats.iv > 0 ? '#4ade80' : '#f87171' }}>
                IV:{dataStats.iv}/{dataStats.total}
              </span>
            </div>
          </div>

          <select value={sym.value}
            onChange={function(e) {
              setSym(SYMBOLS.find(function(s) { return s.value === e.target.value; }));
              setChain([]); setExpiries([]); prevOI.current = {};
            }}
            style={{ background: '#060b18', color: '#e2e8f0', border: '1px solid rgba(26,45,74,0.6)', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontFamily: 'inherit' }}>
            {SYMBOLS.map(function(s) { return <option key={s.value} value={s.value}>{s.label}</option>; })}
          </select>

          <div style={{ display: 'flex', gap: 3 }}>
            {expiries.slice(0, 7).map(function(exp, i) {
              return (
                <button key={exp.date} onClick={function() { setSelExpiry(exp.date); prevOI.current = {}; }}
                  style={Object.assign({}, pill(selExpiry === exp.date), { position: 'relative' })}>
                  {exp.label}
                  {i === 0 && <span style={{ position: 'absolute', top: -3, right: -2, fontSize: 6, background: '#fbbf24', color: '#000', padding: '0 3px', borderRadius: 2, fontWeight: 900, lineHeight: '10px' }}>W</span>}
                </button>
              );
            })}
          </div>

          {spot && (
            <div style={{ marginLeft: isCompact ? 0 : 'auto', display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 9, color: '#556677', fontWeight: 700 }}>{sym.index}</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: '#0ea5e9' }}>{fmtNum(spot, 2)}</span>
              {maxPain && <span style={{ fontSize: 9, color: '#8899aa' }}>MP:<span style={{ color: '#fbbf24', fontWeight: 700 }}>{maxPain}</span></span>}
            </div>
          )}
        </div>

        {/* Row 2 controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 9, color: '#556677', fontWeight: 800 }}>LOTS</span>
            <button onClick={function() { setLots(function(l) { return Math.max(1, l - 1); }); }} style={Object.assign({}, btnS('14,165,233'), { padding: '1px 5px' })}>{'\u2212'}</button>
            <input type="number" min={1} max={100} value={lots}
              onChange={function(e) { setLots(Math.max(1, parseInt(e.target.value) || 1)); }}
              style={{ width: 36, background: '#060b18', color: '#e2e8f0', border: '1px solid rgba(26,45,74,0.6)', borderRadius: 3, padding: '2px 4px', fontSize: 11, fontFamily: 'inherit', textAlign: 'center' }} />
            <button onClick={function() { setLots(function(l) { return l + 1; }); }} style={Object.assign({}, btnS('14,165,233'), { padding: '1px 5px' })}>+</button>
            <span style={{ fontSize: 9, color: '#3d4f63' }}>={'  '}{lots * getLotSize(sym.index)}qty</span>
          </div>
          <div style={{ width: 1, height: 16, background: 'rgba(26,45,74,0.4)' }} />
          {['NRML', 'MIS'].map(function(p) { return <button key={p} onClick={function() { setProduct(p); }} style={pill(product === p, p === 'MIS' ? '251,191,36' : '14,165,233')}>{p}</button>; })}
          <div style={{ width: 1, height: 16, background: 'rgba(26,45,74,0.4)' }} />
          {['MARKET', 'LIMIT'].map(function(t) { return <button key={t} onClick={function() { setOrderType(t); }} style={pill(orderType === t, t === 'MARKET' ? '0,212,170' : '167,139,250')}>{t}</button>; })}
          <div style={{ width: 1, height: 16, background: 'rgba(26,45,74,0.4)' }} />
          <select value={refreshRate} onChange={function(e) { setRefreshRate(Number(e.target.value)); }}
            style={{ background: '#060b18', color: '#6b7a8d', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, padding: '2px 4px', fontSize: 9, fontFamily: 'inherit' }}>
            <option value={1000}>1s</option><option value={2000}>2s</option><option value={3000}>3s</option><option value={5000}>5s</option>
          </select>
          <button onClick={function() { setAutoRefresh(function(a) { return !a; }); }} style={pill(autoRefresh, autoRefresh ? '0,212,170' : '100,100,120')}>
            {autoRefresh ? '\u25CF Auto' : '\u25CB Paused'}
          </button>
          <button onClick={function() { fetchChain(); }} disabled={loading} style={Object.assign({}, pill(false), { color: '#0ea5e9' })}>{'\u27F3'} Refresh</button>
          <select value={depthCount} onChange={function(e) { setDepthCount(Number(e.target.value)); }}
            style={{ background: '#060b18', color: '#6b7a8d', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, padding: '2px 4px', fontSize: 9, fontFamily: 'inherit' }}>
            <option value={10}>10 strikes</option><option value={15}>15 strikes</option><option value={20}>20 strikes</option><option value={25}>25 strikes</option><option value={30}>30 strikes</option>
          </select>
          <div style={{ width: 1, height: 16, background: 'rgba(26,45,74,0.4)' }} />
          <span style={{ fontSize: 9, color: '#556677', fontWeight: 800 }}>FLOW</span>
          <select value={flowLookback} onChange={function(e) { setFlowLookback(Number(e.target.value)); }}
            title="Flow popup lookback window"
            style={{ background: '#060b18', color: '#6b7a8d', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, padding: '2px 4px', fontSize: 9, fontFamily: 'inherit' }}>
            <option value={5}>5m</option><option value={10}>10m</option><option value={15}>15m</option><option value={30}>30m</option>
          </select>
          <button onClick={function() { setShowDebug(function(d) { return !d; }); }}
            style={Object.assign({}, pill(showDebug, '251,191,36'), { fontSize: 8 })}>{'\uD83D\uDD0D'} Debug</button>
          <button onClick={function() { setShowAnalysis(function(a) { return !a; }); }}
            style={Object.assign({}, pill(showAnalysis, '14,165,233'), { fontSize: 9 })}>{'\uD83D\uDCCA'} Analyze</button>
          {(forcedOpen || forcedDays) && (
            <div style={{ display: 'flex', gap: 4 }}>
              {forcedOpen && <span style={{ fontSize: 8, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '2px 5px', borderRadius: 2 }}>{'\u26A1'}FORCED</span>}
              {forcedDays && <span style={{ fontSize: 8, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '2px 5px', borderRadius: 2 }}>{'\u26A1'}WKND</span>}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {['chain', 'positions'].map(function(t) { var label = t === 'chain' ? 'Chain' : 'Positions' + (ocPositions.length ? ' (' + ocPositions.length + ')' : ''); return <button key={t} onClick={function() { setMainTab(t); }} style={{ padding: '5px 16px', fontSize: 11, fontWeight: 800, cursor: 'pointer', border: 'none', fontFamily: 'inherit', background: mainTab === t ? 'rgba(14,165,233,0.08)' : 'transparent', color: mainTab === t ? '#0ea5e9' : '#556677', borderBottom: mainTab === t ? '2px solid #0ea5e9' : '2px solid transparent' }}>{label}</button>; })}
        </div>
      </div>

      {/* Flow Summary — chain-level flow analytics (Volume PCR divergence + Hot vs Pin) */}
      {flowSummary && mainTab === 'chain' && (function() {
        var fs = flowSummary;
        // tone -> chip style
        var toneStyle = function(tone) {
          if (tone === 'bear') return { bg: 'rgba(255,68,102,0.08)', border: 'rgba(255,68,102,0.35)', color: '#ff4466' };
          if (tone === 'bull') return { bg: 'rgba(0,212,170,0.08)', border: 'rgba(0,212,170,0.35)', color: '#00d4aa' };
          if (tone === 'amber') return { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.35)', color: '#fbbf24' };
          if (tone === 'info') return { bg: 'rgba(14,165,233,0.08)', border: 'rgba(14,165,233,0.35)', color: '#0ea5e9' };
          return { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.3)', color: '#94a3b8' };
        };
        // Map divergence value to chip tone
        var divTone = fs.divInterp.tone === 'bear' ? 'bear' : fs.divInterp.tone === 'bull' ? 'bull' : fs.divInterp.tone === 'amber' ? 'amber' : 'neutral';
        // Volume PCR chip tone (mirrors divergence)
        var volPcrTone = fs.divergence >= 0.4 ? 'bear' : fs.divergence <= -0.4 ? 'bull' : Math.abs(fs.divergence) >= 0.15 ? 'amber' : 'neutral';
        // Hot/Pin/Gap chip tone
        var hotTone = fs.hotInterp.tone === 'bear' ? 'bear' : fs.hotInterp.tone === 'bull' ? 'bull' : fs.hotInterp.tone === 'amber' ? 'amber' : 'neutral';

        var chipStyle = function(tone) {
          var s = toneStyle(tone);
          return { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, border: '1px solid ' + s.border, background: s.bg, color: s.color, cursor: 'pointer', fontFamily: 'inherit' };
        };
        var chipLbl = { color: '#6b7a8d', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 2 };

        return (
          <div style={{ padding: '8px 12px', background: '#0a0f1e', borderBottom: '1px solid rgba(26,45,74,0.5)' }}>
            {/* Chip row */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}
                 onClick={function() { setShowFlowPanel(function(s) { return !s; }); }}>
              <button style={chipStyle('info')}><span style={chipLbl}>OI PCR</span><span>{fs.oiPcr.toFixed(2)}</span></button>
              <button style={chipStyle(volPcrTone)}><span style={chipLbl}>Vol PCR</span><span>{fs.volPcr.toFixed(2)}</span></button>
              <button style={chipStyle(divTone)}><span style={chipLbl}>{'\u0394'}</span><span>{fs.divergence >= 0 ? '+' : ''}{fs.divergence.toFixed(2)}</span></button>
              <span style={{ width: 1, height: 14, background: 'rgba(26,45,74,0.5)', margin: '0 4px' }} />
              <button style={chipStyle(hotTone)}><span style={chipLbl}>Hot</span><span>{fs.hottest.strike} {fs.hotSide}</span></button>
              <button style={chipStyle(hotTone)}><span style={chipLbl}>Pin</span><span>{fs.gexPin || '\u2014'}</span></button>
              {fs.gap !== null && <button style={chipStyle(hotTone)}><span style={chipLbl}>Gap</span><span>{fs.gap >= 0 ? '+' : ''}{fs.gap}pts</span></button>}
              <span style={{ width: 1, height: 14, background: 'rgba(26,45,74,0.5)', margin: '0 4px' }} />
              <button onClick={function(e) { e.stopPropagation(); setShowFlowPanel(function(s) { return !s; }); }}
                style={Object.assign({}, chipStyle('neutral'), { fontWeight: 500 })}>
                {showFlowPanel ? '\u25B4' : '\u25BE'} Flow detail
              </button>
            </div>

            {/* Expanded panel */}
            {showFlowPanel && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  {/* PCR Divergence card */}
                  <div style={{ background: '#060b18', border: '1px solid rgba(26,45,74,0.5)', borderRadius: 6, padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 5, borderBottom: '1px solid rgba(26,45,74,0.4)' }}>
                      <span style={{ fontSize: 11, fontWeight: 500, color: '#e2e8f0' }}>PCR divergence</span>
                      <span style={{ fontSize: 9, color: '#6b7a8d' }}>today vs accumulated</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11 }}>
                      <span style={{ color: '#6b7a8d' }}>OI PCR (accumulated)</span>
                      <span style={{ color: '#0ea5e9' }}>{fs.oiPcr.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11 }}>
                      <span style={{ color: '#6b7a8d' }}>Volume PCR (today)</span>
                      <span style={{ color: toneStyle(volPcrTone).color }}>{fs.volPcr.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 4px', fontSize: 11, borderTop: '1px dashed rgba(26,45,74,0.4)', marginTop: 4 }}>
                      <span style={{ color: '#6b7a8d' }}>Divergence</span>
                      <span style={{ color: toneStyle(divTone).color, fontWeight: 500 }}>{fs.divergence >= 0 ? '+' : ''}{fs.divergence.toFixed(2)}</span>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.25)', borderLeft: '2px solid ' + toneStyle(divTone).color, padding: '8px 10px', marginTop: 6, fontSize: 11, lineHeight: 1.45 }}>
                      <div style={{ color: toneStyle(divTone).color, fontWeight: 500, marginBottom: 3 }}>{fs.divInterp.label}</div>
                      <span style={{ color: '#94a3b8' }}>{fs.divInterp.desc}</span>
                    </div>
                  </div>

                  {/* Hot vs Pin card */}
                  <div style={{ background: '#060b18', border: '1px solid rgba(26,45,74,0.5)', borderRadius: 6, padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 5, borderBottom: '1px solid rgba(26,45,74,0.4)' }}>
                      <span style={{ fontSize: 11, fontWeight: 500, color: '#e2e8f0' }}>Hot strike vs GEX pin</span>
                      <span style={{ fontSize: 9, color: '#6b7a8d' }}>{'today\u2019s flow vs structure'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11 }}>
                      <span style={{ color: '#6b7a8d' }}>Hottest strike (vol)</span>
                      <span style={{ color: '#fbbf24' }}>{fs.hottest.strike} ({fmtOI(fs.hottest.total)} vol)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11 }}>
                      <span style={{ color: '#6b7a8d' }}>{'\u2014 side breakdown'}</span>
                      <span style={{ color: '#94a3b8', fontSize: 10 }}>
                        <span style={{ color: '#10b981' }}>PE: {fs.hotPePct.toFixed(0)}%</span>
                        {' \u00B7 '}
                        <span style={{ color: '#fb923c' }}>CE: {(100 - fs.hotPePct).toFixed(0)}%</span>
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11 }}>
                      <span style={{ color: '#6b7a8d' }}>GEX pin (structural)</span>
                      <span style={{ color: '#fbbf24' }}>{fs.gexPin || 'unavailable'}</span>
                    </div>
                    {fs.gap !== null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 4px', fontSize: 11, borderTop: '1px dashed rgba(26,45,74,0.4)', marginTop: 4 }}>
                        <span style={{ color: '#6b7a8d' }}>Gap</span>
                        <span style={{ color: toneStyle(hotTone).color, fontWeight: 500 }}>{fs.gap >= 0 ? '+' : ''}{fs.gap}pts {fs.gap > 0 ? '(above pin)' : fs.gap < 0 ? '(below pin)' : ''}</span>
                      </div>
                    )}
                    <div style={{ background: 'rgba(0,0,0,0.25)', borderLeft: '2px solid ' + toneStyle(hotTone).color, padding: '8px 10px', marginTop: 6, fontSize: 11, lineHeight: 1.45 }}>
                      <div style={{ color: toneStyle(hotTone).color, fontWeight: 500, marginBottom: 3 }}>{fs.hotInterp.label}</div>
                      <span style={{ color: '#94a3b8' }}>{fs.hotInterp.desc}</span>
                    </div>
                  </div>
                </div>

                {/* Top 5 VOR card */}
                <div style={{ background: '#060b18', border: '1px solid rgba(26,45,74,0.5)', borderRadius: 6, padding: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 5, borderBottom: '1px solid rgba(26,45,74,0.4)' }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#e2e8f0' }}>{'Top 5 strikes by VOR (today\u2019s churn)'}</span>
                    <span style={{ fontSize: 9, color: '#6b7a8d' }}>vol {'\u00F7'} oi {'\u00B7'} &gt; 1.0 = active defence</span>
                  </div>
                  {fs.topVor.length === 0 ? (
                    <div style={{ fontSize: 10, color: '#6b7a8d', padding: 8, textAlign: 'center' }}>No VOR data yet. Volume builds up through the session.</div>
                  ) : (
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#6b7a8d', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          <th style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 400 }}>strike</th>
                          <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 400 }}>side</th>
                          <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 400 }}>vol</th>
                          <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 400 }}>oi</th>
                          <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 400 }}>vor</th>
                          <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 400 }}>read</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fs.topVor.map(function(v, i) {
                          var read = v.vor >= 2 ? 'extreme' : v.vor >= 1 ? 'full turnover' : v.vor >= 0.5 ? 'heavy active' : v.vor >= 0.25 ? 'active' : 'building';
                          var vorColor = v.vor >= 1 ? '#ef4444' : v.vor >= 0.5 ? '#fb923c' : v.vor >= 0.25 ? '#fbbf24' : '#94a3b8';
                          return (
                            <tr key={i} style={{ borderTop: '1px solid rgba(26,45,74,0.25)' }}>
                              <td style={{ padding: '3px 4px', color: '#c4cfd8' }}>{v.strike}</td>
                              <td style={{ textAlign: 'right', padding: '3px 4px', color: v.side === 'PE' ? '#10b981' : '#fb923c' }}>{v.side}</td>
                              <td style={{ textAlign: 'right', padding: '3px 4px', color: '#c4cfd8' }}>{fmtOI(v.vol)}</td>
                              <td style={{ textAlign: 'right', padding: '3px 4px', color: '#94a3b8' }}>{fmtOI(v.oi)}</td>
                              <td style={{ textAlign: 'right', padding: '3px 4px', color: vorColor, fontWeight: 500 }}>{v.vor.toFixed(2)}</td>
                              <td style={{ textAlign: 'right', padding: '3px 4px', color: '#94a3b8', fontSize: 10 }}>{read}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Synthesis box */}
                <div style={{ display: 'flex', gap: 8, padding: '8px 10px', background: toneStyle(fs.synth.tone).bg, border: '1px solid ' + toneStyle(fs.synth.tone).border, borderRadius: 6 }}>
                  <span style={{ fontSize: 14, color: toneStyle(fs.synth.tone).color }}>{'\u25C9'}</span>
                  <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.5 }}>
                    <span style={{ color: toneStyle(fs.synth.tone).color, fontWeight: 500 }}>{fs.synth.label}</span>
                    <br/>
                    {fs.synth.desc}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Debug Panel */}
      {showDebug && rawDebug && (function() {
        var rawChain = rawDebug.chain || rawDebug.data || [];
        var sampleRow = rawChain[0] || {};
        // Find an ATM-area row for better diagnosis
        var atmRow = sampleRow;
        if (spot && rawChain.length > 10) {
          var bestDist = Infinity;
          for (var di = 0; di < rawChain.length; di++) {
            var dd = Math.abs((rawChain[di].strike || 0) - spot);
            if (dd < bestDist) { bestDist = dd; atmRow = rawChain[di]; }
          }
        }
        var hasOI = atmRow.ce_oi !== null && atmRow.ce_oi !== undefined && atmRow.ce_oi > 0;
        var hasIV = atmRow.ce_iv !== null && atmRow.ce_iv !== undefined;
        var hasLTP = atmRow.ce_ltp > 0 || atmRow.pe_ltp > 0;
        var hasToken = !!atmRow.ce_token;
        var hasOIChg = atmRow.ce_oi_chg !== null && atmRow.ce_oi_chg !== undefined;

        return (
          <div style={{ padding: '8px 12px', background: '#0d1526', borderBottom: '1px solid rgba(26,45,74,0.5)', maxHeight: 280, overflowY: 'auto', fontSize: 10, color: '#8899aa' }}>
            <div style={{ marginBottom: 6, fontWeight: 800, color: '#fbbf24' }}>
              DATA DEBUG — Source: {dataSource} — Raw: {rawChain.length} rows — Filtered: {chain.length} rows
            </div>

            {/* Diagnosis */}
            <div style={{ marginBottom: 8, padding: '6px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(26,45,74,0.4)' }}>
              <div style={{ fontWeight: 800, marginBottom: 4, color: '#e2e8f0' }}>DIAGNOSIS (ATM strike: {atmRow.strike || '?'})</div>
              <div style={{ color: hasToken ? '#4ade80' : '#f87171' }}>{hasToken ? '\u2713' : '\u2717'} ce_token: {hasToken ? atmRow.ce_token : 'MISSING \u2014 live tick overlay won\'t work'}</div>
              <div style={{ color: hasLTP ? '#4ade80' : '#f87171' }}>{hasLTP ? '\u2713' : '\u2717'} LTP: ce={atmRow.ce_ltp} pe={atmRow.pe_ltp} {!hasLTP ? '\u2014 backend not fetching prices (use kite.quote())' : ''}</div>
              <div style={{ color: hasOI ? '#4ade80' : '#f87171' }}>{hasOI ? '\u2713' : '\u2717'} OI: ce={JSON.stringify(atmRow.ce_oi)} pe={JSON.stringify(atmRow.pe_oi)} {!hasOI ? '\u2014 BACKEND RETURNS NULL! Use kite.quote() not kite.ltp()' : ''}</div>
              <div style={{ color: hasIV ? '#4ade80' : '#fbbf24' }}>{hasIV ? '\u2713' : '\u25CB'} IV: ce={JSON.stringify(atmRow.ce_iv)} {!hasIV ? '\u2014 OK, calculated client-side from LTP (needs market open)' : ''}</div>
              <div style={{ color: hasOIChg ? '#4ade80' : '#fbbf24' }}>{hasOIChg ? '\u2713' : '\u25CB'} OI Change: field={atmRow.ce_oi_chg !== undefined ? 'ce_oi_chg' : (atmRow.ce_oi_change !== undefined ? 'ce_oi_change' : 'missing')} val={JSON.stringify(atmRow.ce_oi_chg || atmRow.ce_oi_change)}</div>
            </div>

            {!hasOI && !hasLTP && (
              <div style={{ padding: '6px 8px', borderRadius: 4, background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.3)', color: '#ff4466', marginBottom: 6 }}>
                {'\u26A0'} ALL DATA IS NULL. Your backend /optionchain endpoint is NOT using kite.quote().
                Deploy algrok_backend_fix.py on your EC2 to fix this.
                The fix uses kite.quote() which returns OI, volume, and LTP.
              </div>
            )}

            <div style={{ marginBottom: 4, color: '#556677' }}>
              Top-level keys: {Object.keys(rawDebug).filter(function(k) { return k !== 'chain'; }).join(', ')}
              {' \u2014 '} Row keys: {Object.keys(sampleRow).join(', ')}
            </div>
            <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: 9, color: '#6b7a8d', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              ATM SAMPLE ({atmRow.strike}): {JSON.stringify(atmRow, null, 1)}
            </pre>
          </div>
        );
      })()}

      {/* Analysis Panel */}
      {showAnalysis && liveChain.length > 0 && (
        <OptionAnalysis chain={liveChain} spot={spot} onClose={function() { setShowAnalysis(false); }} />
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: (isCompact && showBuilder && mainTab === 'chain') ? 'column' : 'row', minHeight: 0 }}>

        {mainTab === 'chain' && (<>
        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }} onScroll={function() { userScrolled.current = true; }}>
          {liveChain.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#3d4f63', fontSize: 12 }}>
              {loading ? 'Loading option chain...' : 'Select expiry to load chain'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', width: 28 })}>+</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', color: '#ff4466', width: 48 })}>BUY</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', color: '#ff4466', width: 48 })}>SELL</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'right' })}>OI Chg</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'right' })}>OI</th>
                  {!isCompact && <th style={Object.assign({}, S.th, { textAlign: 'right', width: 60 })}>OI Bar</th>}
                  {!isCompact && <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Vol</th>}
                  <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Chg%</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'right', color: '#e2e8f0' })}>LTP</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'right', color: '#ff6b4a' })}>IV%</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', color: '#0ea5e9', width: 80, background: '#080d1a', borderLeft: '2px solid rgba(14,165,233,0.3)', borderRight: '2px solid rgba(14,165,233,0.3)' })}>STRIKE</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'left', color: '#4ade80' })}>IV%</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'left', color: '#e2e8f0' })}>LTP</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'left' })}>Chg%</th>
                  {!isCompact && <th style={Object.assign({}, S.th, { textAlign: 'left' })}>Vol</th>}
                  {!isCompact && <th style={Object.assign({}, S.th, { textAlign: 'left', width: 60 })}>OI Bar</th>}
                  <th style={Object.assign({}, S.th, { textAlign: 'left' })}>OI</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'left' })}>OI Chg</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', color: '#00d4aa', width: 48 })}>BUY</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', color: '#00d4aa', width: 48 })}>SELL</th>
                  <th style={Object.assign({}, S.th, { textAlign: 'center', width: 28 })}>+</th>
                </tr>
              </thead>
              <tbody>
                {liveChain.map(function(row) {
                  var isATM = atmStrike && row.strike === atmStrike.strike;
                  var isMP = maxPain && row.strike === maxPain;
                  var itm_ce = spot && row.strike < spot;
                  var rowBg = isATM ? 'rgba(14,165,233,0.06)' : itm_ce ? 'rgba(255,255,255,0.015)' : 'transparent';

                  return (
                    <tr key={row.strike} ref={isATM ? atmRef : null} style={{ background: rowBg }}>
                      <td style={td({ textAlign: 'center' })}>{isInBuilder(row, 'CE') ? <button onClick={function() { removeLeg(row.ce_symbol); }} style={Object.assign({}, btnS('251,191,36'), { fontSize: 11 })}>-</button> : <button onClick={function() { addLeg(row.strike, 'CE', 'BUY'); }} style={Object.assign({}, btnS('14,165,233'), { fontSize: 11 })}>+</button>}</td>
                      <td style={td({ textAlign: 'center' })}><button onClick={function() { handleOrderClick(row.strike, 'CE', 'BUY', row.ce_ltp || 0); }} style={btnS('0,180,140')}>B</button></td>
                      <td style={td({ textAlign: 'center' })}><button onClick={function() { handleOrderClick(row.strike, 'CE', 'SELL', row.ce_ltp || 0); }} style={btnS('255,68,102')}>S</button></td>
                      <td style={td({ color: oiChangeColor(row.ce_oi_change), textAlign: 'right', fontSize: 10 })}>
                        {row.ce_oi_change != null ? (row.ce_oi_change > 0 ? '+' : '') + fmtOI(row.ce_oi_change) : '\u2014'}
                      </td>
                      <td style={td({ textAlign: 'right', fontWeight: (row.ce_oi || 0) > maxCEOI * 0.7 ? 700 : 400, color: row.ce_oi ? '#c4cfd8' : '#3d4f63' })}>
                        {fmtOI(row.ce_oi)}
                      </td>
                      {!isCompact && <td style={td({ paddingTop: 8, textAlign: 'right' })}><OIBar value={row.ce_oi || 0} max={maxCEOI} side="CE" /></td>}
                      {!isCompact && <td style={td({ color: '#6b7a8d', textAlign: 'right', fontSize: 10 })}>{fmtOI(row.ce_volume)}</td>}
                      <td style={td({ color: pnlColor(row.ce_change_pct), textAlign: 'right', fontSize: 10 })}>{fmtPct(row.ce_change_pct)}</td>
                      <td style={td({ color: '#e2e8f0', fontWeight: 700, textAlign: 'right' })}>
                        <FlashCell value={row.ce_ltp} format={function(v) { return fmtNum(v); }} prevRef={prevPrices} flashKey={row.strike + '_CE'} />
                      </td>
                      <td style={td({ color: '#ff8866', textAlign: 'right', fontSize: 10 })}>
                        {row.ce_iv != null ? fmtNum(row.ce_iv, 1) : '\u2014'}
                      </td>
                      <td onClick={function(e) { handleStrikeClick(row.strike, e); }} title="Click for flow detail" style={{
                        padding: '4px 6px', fontSize: 11, fontWeight: 900, textAlign: 'center', whiteSpace: 'nowrap',
                        borderBottom: '1px solid rgba(26,45,74,0.15)',
                        color: isATM ? '#0ea5e9' : isMP ? '#fbbf24' : '#e2e8f0',
                        background: isATM ? 'rgba(14,165,233,0.1)' : '#080d1a',
                        borderLeft: '2px solid rgba(14,165,233,0.2)', borderRight: '2px solid rgba(14,165,233,0.2)',
                        cursor: 'pointer', userSelect: 'none',
                      }}>
                        {row.strike}
                        {isATM && <div style={{ fontSize: 7, color: '#0ea5e9', fontWeight: 900, marginTop: -1 }}>ATM</div>}
                        {isMP && !isATM && <div style={{ fontSize: 7, color: '#fbbf24', fontWeight: 700, marginTop: -1 }}>MP</div>}
                      </td>
                      <td style={td({ color: '#66ccaa', fontSize: 10 })}>{row.pe_iv != null ? fmtNum(row.pe_iv, 1) : '\u2014'}</td>
                      <td style={td({ color: '#e2e8f0', fontWeight: 700 })}>
                        <FlashCell value={row.pe_ltp} format={function(v) { return fmtNum(v); }} prevRef={prevPrices} flashKey={row.strike + '_PE'} />
                      </td>
                      <td style={td({ color: pnlColor(row.pe_change_pct), fontSize: 10 })}>{fmtPct(row.pe_change_pct)}</td>
                      {!isCompact && <td style={td({ color: '#6b7a8d', fontSize: 10 })}>{fmtOI(row.pe_volume)}</td>}
                      {!isCompact && <td style={td({ paddingTop: 8 })}><OIBar value={row.pe_oi || 0} max={maxPEOI} side="PE" /></td>}
                      <td style={td({ fontWeight: (row.pe_oi || 0) > maxPEOI * 0.7 ? 700 : 400, color: row.pe_oi ? '#c4cfd8' : '#3d4f63' })}>{fmtOI(row.pe_oi)}</td>
                      <td style={td({ color: oiChangeColor(row.pe_oi_change), fontSize: 10 })}>
                        {row.pe_oi_change != null ? (row.pe_oi_change > 0 ? '+' : '') + fmtOI(row.pe_oi_change) : '\u2014'}
                      </td>
                      <td style={td({ textAlign: 'center' })}><button onClick={function() { handleOrderClick(row.strike, 'PE', 'BUY', row.pe_ltp || 0); }} style={btnS('0,180,140')}>B</button></td>
                      <td style={td({ textAlign: 'center' })}><button onClick={function() { handleOrderClick(row.strike, 'PE', 'SELL', row.pe_ltp || 0); }} style={btnS('255,68,102')}>S</button></td>
                      <td style={td({ textAlign: 'center' })}>{isInBuilder(row, 'PE') ? <button onClick={function() { removeLeg(row.pe_symbol); }} style={Object.assign({}, btnS('251,191,36'), { fontSize: 11 })}>-</button> : <button onClick={function() { addLeg(row.strike, 'PE', 'SELL'); }} style={Object.assign({}, btnS('14,165,233'), { fontSize: 11 })}>+</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Strategy Builder Panel */}
        {showBuilder && builderLegs.length > 0 && (
          <div style={{ width: isCompact ? '100%' : 300, flexShrink: 0, borderLeft: isCompact ? 'none' : '1px solid rgba(26,45,74,0.4)', borderTop: isCompact ? '1px solid rgba(26,45,74,0.4)' : 'none', padding: 10, overflowY: 'auto', background: '#070c1b', maxHeight: isCompact ? 220 : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800 }}>Strategy Builder</span>
              <button onClick={function() { setShowBuilder(false); setBuilderLegs([]); }} style={Object.assign({}, btnS('100,100,120'), { fontSize: 8 })}>X</button>
            </div>
            {builderLegs.map(function(leg, i) {
              return <div key={leg.symbol} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 0', borderBottom: '1px solid rgba(26,45,74,0.15)', fontSize: 10 }}>
                <button onClick={function() { toggleSide(i); }} style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 800, cursor: 'pointer', border: 'none', background: leg.side === 'BUY' ? 'rgba(0,212,170,0.15)' : 'rgba(255,68,102,0.15)', color: leg.side === 'BUY' ? '#00d4aa' : '#ff4466' }}>{leg.side}</button>
                <span style={{ fontWeight: 700, flex: 1 }}>{leg.strike} {leg.type}</span>
                <span style={{ color: '#6b7a8d' }}>{leg.ltp ? leg.ltp.toFixed(1) : '--'}</span>
                <button onClick={function() { setLegLots(i, leg.lots - 1); }} style={Object.assign({}, btnS('14,165,233'), { padding: '0 4px', fontSize: 9 })}>-</button>
                <span style={{ fontWeight: 900, minWidth: 14, textAlign: 'center' }}>{leg.lots}</span>
                <button onClick={function() { setLegLots(i, leg.lots + 1); }} style={Object.assign({}, btnS('14,165,233'), { padding: '0 4px', fontSize: 9 })}>+</button>
                <button onClick={function() { removeLeg(leg.symbol); }} style={Object.assign({}, btnS('251,191,36'), { padding: '0 4px', fontSize: 9 })}>x</button>
              </div>;
            })}
            <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 4, margin: '8px 0' }}>
              {[{l:'CREDIT',v:fmtRs(builderGreeks.credit),c:builderGreeks.credit>=0?'#00d4aa':'#ff4466'},{l:'DELTA',v:builderGreeks.delta,c:'#e2e8f0'},{l:'THETA',v:builderGreeks.theta,c:builderGreeks.theta>=0?'#00d4aa':'#ff4466'},{l:'MAX LOSS',v:fmtRs(bpMin),c:'#ff4466'}].map(function(g){return <div key={g.l} style={{background:'rgba(0,0,0,0.2)',borderRadius:4,padding:4,textAlign:'center'}}><div style={{fontSize:7,color:'#556677'}}>{g.l}</div><div style={{fontSize:11,fontWeight:800,color:g.c}}>{g.v}</div></div>;})}
            </div>
            {builderPayoff.length > 0 && <div style={{ background: 'rgba(12,18,32,0.95)', borderRadius: 6, border: '1px solid rgba(26,45,74,0.3)', padding: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 7, color: '#556677', fontWeight: 700, marginBottom: 2 }}>PAYOFF AT EXPIRY</div>
              <svg viewBox="0 0 280 80" style={{ width: '100%', height: 80 }}>
                {function() { var range = bpMax - bpMin || 1; var zy = 5 + ((bpMax - 0) / range) * 70; return <line x1="5" y1={zy} x2="275" y2={zy} stroke="rgba(100,120,140,0.3)" strokeWidth="0.5" />; }()}
                <polyline fill="none" stroke="#00d4aa" strokeWidth="1.5" points={builderPayoff.map(function(p, i) { var x = 5 + (i / (builderPayoff.length - 1)) * 270; var range = bpMax - bpMin || 1; var y = 5 + ((bpMax - p.y) / range) * 70; return x + ',' + y; }).join(' ')} />
                {spot > 0 && builderPayoff.length > 0 && function() { var minX = builderPayoff[0].x, maxX = builderPayoff[builderPayoff.length - 1].x; var sx = 5 + ((spot - minX) / (maxX - minX)) * 270; if (sx >= 5 && sx <= 275) return <line x1={sx} y1="2" x2={sx} y2="78" stroke="#fbbf24" strokeWidth="0.5" strokeDasharray="3" />; return null; }()}
              </svg>
            </div>}
            <div style={{ fontSize: 9, color: '#556677', marginBottom: 6 }}>BUY first, then SELL | {orderType === 'MARKET' ? 'MKT prot 5\u21927\u219210%' : 'LIMIT +2%\u21925%\u21927%'}</div>
            <button onClick={placeStrategy} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(14,165,233,0.3)', background: 'rgba(14,165,233,0.12)', color: '#0ea5e9', fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>Place ({builderLegs.length} legs)</button>
          </div>
        )}
        </>)}

        {mainTab === 'positions' && (<>
        {/* Order Log - Left */}
        <div style={{ flex: 1, overflowY: 'auto', borderRight: '1px solid rgba(26,45,74,0.3)' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(26,45,74,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#556677' }}>ORDER LOG</span>
            <span style={{ fontSize: 9, color: '#3d4f63' }}>{ocLog.length} entries</span>
          </div>
          <div style={{ padding: '6px 10px' }}>
            {ocLog.length === 0 ? <div style={{ color: '#3d4f63', fontSize: 10, padding: 20, textAlign: 'center' }}>No OC orders yet</div> : ocLog.slice().reverse().map(function(e, i) {
              var c = e.level === 'error' ? '#ff4466' : e.level === 'warning' ? '#fbbf24' : e.msg && e.msg.indexOf('\u2713') >= 0 ? '#00d4aa' : '#0ea5e9';
              return <div key={i} style={{ fontSize: 9, padding: '3px 0', borderBottom: '1px solid rgba(26,45,74,0.08)', fontFamily: 'monospace' }}><span style={{ color: '#3d4f63', marginRight: 4 }}>{e.ts}</span><span style={{ color: c }}>{e.msg}</span></div>;
            })}
          </div>
        </div>

        {/* Positions - Right */}
        <div style={{ flex: 1.2, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid rgba(26,45,74,0.25)' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#556677' }}>POSITIONS</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: ocMtm >= 0 ? '#00d4aa' : '#ff4466' }}>MTM: {fmtRs(ocMtm)}</span>
              {ocPositions.length > 0 && <button onClick={function() { if (window.confirm('Exit ALL OC positions?')) ocDoApi('/oc-exit-all', { reason: 'Manual', order_mode: orderType.toLowerCase() }); }} style={Object.assign({}, btnS('255,68,102'), { fontSize: 8, padding: '3px 10px' })}>Exit all</button>}
            </div>
          </div>
          <div style={{ padding: '8px 10px' }}>
            {ocPositions.map(function(leg, i) {
              return <div key={i} style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(26,45,74,0.08)', border: '1px solid rgba(26,45,74,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>{leg.strike} {leg.type}</span>
                  <span style={{ fontSize: 8, padding: '1px 6px', borderRadius: 2, fontWeight: 800, background: leg.side === 'BUY' ? 'rgba(0,212,170,0.12)' : 'rgba(255,68,102,0.12)', color: leg.side === 'BUY' ? '#00d4aa' : '#ff4466' }}>{leg.side} x{leg.qty}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#6b7a8d', marginBottom: 5 }}>
                  <span>Entry: {'\u20B9'}{leg.entry_price ? leg.entry_price.toFixed(1) : '--'}</span>
                  <span>LTP: <span style={{ color: pnlColor(leg.pnl) }}>{'\u20B9'}{leg.ltp ? leg.ltp.toFixed(1) : '--'}</span></span>
                  <span style={{ fontWeight: 700, color: pnlColor(leg.pnl || 0) }}>{fmtRs(leg.pnl || 0)}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 8, color: '#556677' }}>SL:</span>
                  <select value={leg.sl_type || 'none'} onChange={function(e) { ocDoApi('/oc-update-leg', { index: i, sl_type: e.target.value, sl_value: leg.sl_value || 0 }); }} style={{ padding: '2px 4px', borderRadius: 3, background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', color: '#e2e8f0', fontSize: 9 }}><option value="none">--</option><option value="%">%</option><option value="points">Pt</option></select>
                  {leg.sl_type && leg.sl_type !== 'none' && <input type="number" defaultValue={leg.sl_value || ''} onBlur={function(e) { ocDoApi('/oc-update-leg', { index: i, sl_type: leg.sl_type, sl_value: Number(e.target.value) }); }} style={{ width: 36, padding: '2px 3px', background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, color: '#e2e8f0', fontSize: 9, textAlign: 'center' }} />}
                  <span style={{ fontSize: 8, color: '#556677', marginLeft: 6 }}>TGT:</span>
                  <select value={leg.tgt_type || 'none'} onChange={function(e) { ocDoApi('/oc-update-leg', { index: i, tgt_type: e.target.value, tgt_value: leg.tgt_value || 0 }); }} style={{ padding: '2px 4px', borderRadius: 3, background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', color: '#e2e8f0', fontSize: 9 }}><option value="none">--</option><option value="%">%</option><option value="points">Pt</option></select>
                  {leg.tgt_type && leg.tgt_type !== 'none' && <input type="number" defaultValue={leg.tgt_value || ''} onBlur={function(e) { ocDoApi('/oc-update-leg', { index: i, tgt_type: leg.tgt_type, tgt_value: Number(e.target.value) }); }} style={{ width: 36, padding: '2px 3px', background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, color: '#e2e8f0', fontSize: 9, textAlign: 'center' }} />}
                  <span style={{ fontSize: 8, color: '#556677', marginLeft: 6 }}>Trail:</span>
                  <select value={leg.trail_type || 'none'} onChange={function(e) { ocDoApi('/oc-update-leg', { index: i, trail_type: e.target.value, trail_value: leg.trail_value || 0 }); }} style={{ padding: '2px 4px', borderRadius: 3, background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', color: '#e2e8f0', fontSize: 9 }}><option value="none">--</option><option value="%">%</option><option value="points">Pt</option></select>
                  {leg.trail_type && leg.trail_type !== 'none' && <input type="number" defaultValue={leg.trail_value || ''} onBlur={function(e) { ocDoApi('/oc-update-leg', { index: i, trail_type: leg.trail_type, trail_value: Number(e.target.value) }); }} style={{ width: 36, padding: '2px 3px', background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, color: '#e2e8f0', fontSize: 9, textAlign: 'center' }} />}
                  <div style={{ flex: 1 }} />
                  <button onClick={function() { ocDoApi('/oc-exit-leg', { index: i, order_mode: orderType.toLowerCase() }); }} style={Object.assign({}, btnS('255,68,102'), { fontSize: 8, padding: '2px 8px' })}>EXIT</button>
                </div>
              </div>;
            })}
            {!ocPositions.length && <div style={{ color: '#3d4f63', fontSize: 11, padding: 30, textAlign: 'center' }}>No open positions from Option Chain</div>}
          </div>

          {/* MTM Controls */}
          {ocPositions.length > 0 && <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid rgba(26,45,74,0.25)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: '#556677' }}>MTM CONTROLS</span>
            {['sl', 'tgt', 'trail'].map(function(k) {
              var mc = ocState ? ocState.mtm_config || {} : {};
              var tKey = k + '_type'; var vKey = k + '_value';
              return <div key={k} style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <span style={{ fontSize: 9, color: '#556677' }}>{k === 'sl' ? 'SL' : k === 'tgt' ? 'TGT' : 'Trail'}:</span>
                <select value={mc[tKey] || 'none'} onChange={function(e) { var u = {}; u[tKey] = e.target.value; ocDoApi('/oc-update-mtm', u); }} style={{ padding: '2px 4px', borderRadius: 3, background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', color: '#e2e8f0', fontSize: 9 }}><option value="none">None</option><option value="%">%</option><option value="points">Pts</option></select>
                {mc[tKey] && mc[tKey] !== 'none' && <input type="number" defaultValue={mc[vKey] || ''} onBlur={function(e) { var u = {}; u[vKey] = Number(e.target.value); ocDoApi('/oc-update-mtm', u); }} style={{ width: 40, padding: '2px 3px', background: '#060b18', border: '1px solid rgba(26,45,74,0.4)', borderRadius: 3, color: '#e2e8f0', fontSize: 9, textAlign: 'center' }} />}
              </div>;
            })}
          </div>}

          {/* Live Payoff */}
          {ocPositions.length > 0 && (function() {
            var pts = []; var center = spot ? Math.round(spot / sym.step) * sym.step : 0;
            if (!center) return null;
            for (var x = center - 800; x <= center + 800; x += 10) {
              var pnl = 0;
              ocPositions.forEach(function(l) { var intr = l.type === 'CE' ? Math.max(0, x - l.strike) : Math.max(0, l.strike - x); if (l.side === 'BUY') pnl += (intr - l.entry_price) * l.qty; else pnl += (l.entry_price - intr) * l.qty; });
              pts.push({ x: x, y: Math.round(pnl) });
            }
            if (!pts.length) return null;
            var mn = Math.min.apply(null, pts.map(function(p) { return p.y; }));
            var mx = Math.max.apply(null, pts.map(function(p) { return p.y; }));
            var range = mx - mn || 1; var zy = 8 + ((mx - 0) / range) * 120;
            return <div style={{ padding: '8px 12px' }}>
              <div style={{ background: 'rgba(12,18,32,0.95)', borderRadius: 6, border: '1px solid rgba(26,45,74,0.25)', padding: 10 }}>
                <div style={{ fontSize: 8, color: '#556677', fontWeight: 700, marginBottom: 4 }}>LIVE PAYOFF</div>
                <svg viewBox="0 0 500 140" style={{ width: '100%', height: 140 }}>
                  <defs><linearGradient id="ocPG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00d4aa" stopOpacity="0.15" /><stop offset="100%" stopColor="#00d4aa" stopOpacity="0" /></linearGradient></defs>
                  <line x1="30" y1={zy} x2="490" y2={zy} stroke="rgba(100,120,140,0.25)" strokeWidth="0.5" />
                  <text x="28" y={zy - 3} fill="#556677" fontSize="7" textAnchor="end">0</text>
                  {function() { var profPts = ''; pts.forEach(function(p, i) { var x = 30 + (i / (pts.length - 1)) * 460; var y = 8 + ((mx - p.y) / range) * 120; if (p.y >= 0) profPts += x + ',' + y + ' '; }); if (profPts) { var f = profPts.split(' ')[0]; var l = profPts.trim().split(' ').pop(); return <polygon fill="url(#ocPG)" points={profPts + ' ' + l.split(',')[0] + ',' + zy + ' ' + f.split(',')[0] + ',' + zy} />; } return null; }()}
                  <polyline fill="none" stroke="#00d4aa" strokeWidth="2" points={pts.map(function(p, i) { var x = 30 + (i / (pts.length - 1)) * 460; var y = 8 + ((mx - p.y) / range) * 120; return x + ',' + y; }).join(' ')} />
                  {spot > 0 && function() { var minX = pts[0].x, maxX = pts[pts.length - 1].x; var sx = 30 + ((spot - minX) / (maxX - minX)) * 460; if (sx >= 30 && sx <= 490) return <g><line x1={sx} y1="3" x2={sx} y2="133" stroke="#fbbf24" strokeWidth="1" strokeDasharray="4" /><text x={sx} y="7" fill="#fbbf24" fontSize="7" textAnchor="middle">SPOT</text></g>; return null; }()}
                </svg>
              </div>
            </div>;
          })()}

          {/* Summary */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(26,45,74,0.25)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 4, padding: '4px 6px', textAlign: 'center' }}><div style={{ fontSize: 7, color: '#556677' }}>PCR</div><div style={{ fontSize: 12, fontWeight: 800, color: pcr > 1.2 ? '#00d4aa' : pcr < 0.8 ? '#ff4466' : '#fbbf24' }}>{pcr.toFixed(2)}</div></div>
              <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 4, padding: '4px 6px', textAlign: 'center' }}><div style={{ fontSize: 7, color: '#556677' }}>CE OI</div><div style={{ fontSize: 10, fontWeight: 700, color: '#ff4466' }}>{fmtOI(totalCEOI)}</div></div>
              <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 4, padding: '4px 6px', textAlign: 'center' }}><div style={{ fontSize: 7, color: '#556677' }}>PE OI</div><div style={{ fontSize: 10, fontWeight: 700, color: '#00d4aa' }}>{fmtOI(totalPEOI)}</div></div>
            </div>
          </div>
        </div>
        </>)}

      </div>


      {/* Flow Popup (Chain Flow framework — per strike) */}
      {flowPopup && <FlowPopup strike={flowPopup.strike} anchorY={flowPopup.anchorY} lookback={flowLookback} onClose={function() { setFlowPopup(null); }} />}

      {/* Confirmation Modal */}
      {confirmOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={function() { setConfirmOrder(null); }}>
          <div style={{ background: '#0d1526', border: '1px solid rgba(26,45,74,0.8)', borderRadius: 8, padding: 20, minWidth: 320, maxWidth: 400 }}
            onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Confirm Order</div>
            <div style={{ fontSize: 12, color: '#8899aa', marginBottom: 16, lineHeight: '20px' }}>
              <strong style={{ color: confirmOrder.action === 'BUY' ? '#00d4aa' : '#ff4466' }}>{confirmOrder.action}</strong>
              {' '}{lots} lot(s) of <strong style={{ color: '#e2e8f0' }}>{sym.index} {confirmOrder.strike} {confirmOrder.optType}</strong>
              <div style={{ marginTop: 4 }}>Type: <strong>{orderType}</strong> {'\u00B7'} Product: <strong>{product}</strong> {'\u00B7'} Qty: <strong>{lots * getLotSize(sym.index)}</strong></div>
              {confirmOrder.ltp > 0 && <div style={{ marginTop: 4 }}>LTP: {'\u20B9'}{fmtNum(confirmOrder.ltp)}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={function() { setConfirmOrder(null); }}
                style={{ flex: 1, padding: '8px 0', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 700, background: 'rgba(100,100,120,0.15)', color: '#8899aa', border: '1px solid rgba(100,100,120,0.3)', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={function() { var o = confirmOrder; setConfirmOrder(null); placeOrder(o.strike, o.optType, o.action, o.ltp); }}
                style={{ flex: 1, padding: '8px 0', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 800, fontFamily: 'inherit', background: confirmOrder.action === 'BUY' ? 'rgba(0,212,170,0.2)' : 'rgba(255,68,102,0.2)', color: confirmOrder.action === 'BUY' ? '#00d4aa' : '#ff4466', border: '1px solid ' + (confirmOrder.action === 'BUY' ? 'rgba(0,212,170,0.4)' : 'rgba(255,68,102,0.4)') }}>
                {confirmOrder.action === 'BUY' ? '\u25B2' : '\u25BC'} {confirmOrder.action}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
